<template>
  <div class="col-span-12">
    <!-- 主要内容区 -->
    <main class="w-full h-full flex flex-col overflow-hidden">
      <!-- 页头导航 - 始终显示 -->
      <header class="bg-surface-light dark:bg-surface-dark/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 h-20 flex items-center flex-shrink-0 z-10 sticky top-0">
        <button @click="goBack" class="w-10 h-10 mr-6 ml-6 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-500 hover:text-primary hover:border-primary hover:shadow-sm transition-all flex items-center justify-center group">
          <span class="material-symbols-outlined text-[20px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
        </button>
        <div>
          <h2 class="text-xl font-bold tracking-tight text-slate-900 dark:text-white mb-1">{{ streamerInfo.name || $t('analyticsSingle.title') }}</h2>
          <div class="flex items-center gap-2">
            <span :class="[
              'flex h-2 w-2 rounded-full',
              isLive ? 'bg-green-500 animate-pulse' : 'bg-slate-300 dark:bg-slate-500'
            ]"></span>
            <p class="text-[11px] font-bold text-slate-500 dark:text-slate-400 tracking-wide uppercase">
              {{ isLive ? $t('analyticsSingle.live') : (hasLiveSessions ? $t('analyticsSingle.analysis') : $t('analyticsSingle.noHistory')) }}
            </p>
          </div>
        </div>
      </header>

      <!-- 内容区域 -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- 加载状态 -->
        <div v-if="loading" class="text-center py-12">
          <div class="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p class="text-lg text-gray-600">{{ $t('analyticsSingle.loading') }}</p>
        </div>

        <!-- 错误状态 -->
        <div v-else-if="error" class="text-center py-12">
          <i class="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
          <p class="text-lg text-gray-600 mb-4">{{ error }}</p>
          <button @click="loadAnalyticsData" class="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors">
            {{ $t('analyticsSingle.retry') }}
          </button>
        </div>

        <!-- 空状态 - 当没有直播会话数据时显示 -->
        <div v-else-if="!hasLiveSessions" class="h-full p-8">
          <div class="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-9 gap-8 h-full">
            <div class="lg:col-span-3 flex-shrink-0 space-y-5 flex flex-col">
              <div class="flex items-center justify-between">
                <h3 class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">{{ $t('analyticsSingle.sessionNodes') }}</h3>
                <span class="text-[10px] font-mono text-slate-300">{{ $t('analyticsSingle.liveFeed') }}</span>
              </div>
              <!-- 无数据时的占位符 -->
              <div class="flex-1 min-h-[200px] flex flex-col items-center justify-center p-6 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl bg-white/50 dark:bg-slate-800/30">
                <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3 text-slate-400">
                  <span class="material-symbols-outlined text-[20px]">calendar_today</span>
                </div>
                <p class="text-xs font-bold text-slate-500 dark:text-slate-400">{{ $t('analyticsSingle.noPastSessions') }}</p>
              </div>
            </div>
            <div class="lg:col-span-6 flex flex-col h-full">
              <div class="flex-1 bg-white dark:bg-surface-dark rounded-2xl border border-slate-100 dark:border-slate-800 shadow-soft flex flex-col items-center justify-center p-12 text-center">
                <div class="relative mb-8">
                  <div class="w-24 h-24 bg-primary/5 rounded-full flex items-center justify-center relative z-10">
                    <div class="absolute inset-0 bg-primary/10 rounded-full animate-ping opacity-75"></div>
                    <span class="material-symbols-outlined text-primary text-[40px] animate-pulse">sensors</span>
                  </div>
                  <div class="absolute -right-4 -top-2 bg-white dark:bg-surface-dark p-2 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 transform rotate-12">
                    <span class="material-symbols-outlined text-emerald-500 text-[18px]">graphic_eq</span>
                  </div>
                  <div class="absolute -left-4 -bottom-2 bg-white dark:bg-surface-dark p-2 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 transform -rotate-12">
                    <span class="material-symbols-outlined text-slate-400 text-[18px]">mic_off</span>
                  </div>
                </div>
                <h2 class="text-2xl font-bold text-slate-900 dark:text-white mb-3">{{ $t('analyticsSingle.roomTracked') }}</h2>
                <p class="text-slate-500 dark:text-slate-400 max-w-md mx-auto leading-relaxed mb-8">
                  {{ $t('analyticsSingle.broadcastStartMessage') }}
                  <br/><span class="text-primary font-medium">{{ $t('analyticsSingle.stayTuned') }}</span>
                </p>
                <button class="bg-primary hover:bg-primaryDark text-white font-bold text-sm py-3 px-6 rounded-xl shadow-lg shadow-primary/20 flex items-center gap-2 transition-all transform hover:-translate-y-0.5 group">
                  <span class="material-symbols-outlined text-[20px] group-hover:rotate-12 transition-transform">dashboard_customize</span>
                  {{ $t('analyticsSingle.viewDashboard') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分析内容 -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4 md:gap-6">
          <!-- 左侧边栏 - Session Nodes -->
          <div class="session-sidebar relative h-full group/sidebar z-10 bg-slate-50/50 dark:bg-slate-900/10 backdrop-blur-sm rounded-l-2xl overflow-hidden"
               :class="{ 
                 'session-sidebar-collapsed': !sessionNodesExpanded,
                 'xl:col-span-3': sessionNodesExpanded,
                 'xl:col-span-1 min-w-[84px]': !sessionNodesExpanded
               }">
            <!-- 最小化模式 -->
            <div class="minimized-content flex flex-col items-center gap-5 py-6 px-2 w-[84px]" v-show="!sessionNodesExpanded">
              <!-- view_sidebar 图标 - 点击可展开 -->
              <div class="h-6 flex items-center justify-center mb-2 text-slate-400 cursor-pointer hover:text-primary transition-colors" @click="toggleSessionNodes" :title="$t('analyticsSingle.expandSidebar')">
                <span class="material-symbols-outlined text-[20px]">view_sidebar</span>
              </div>
              <!-- 显示场次编号的小卡片 -->
              <div v-for="(session, index) in liveSessionData.sessions.slice(0, 5)" :key="`${session.sessionId}-${index}`"
                   :class="[
                     'relative w-12 h-12 rounded-xl flex flex-col items-center justify-center shadow-sm cursor-pointer transition-all',
                     index === selectedSessionIndex
                       ? 'border-2 border-primary shadow-lg shadow-primary/10 bg-white dark:bg-slate-800'
                       : 'border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary/50 hover:text-primary'
                   ]"
                   @click="selectSession(session, index)">
                <!-- 绿点只在正在直播的场次显示 -->
                <span class="absolute -top-1 -right-1 flex h-2.5 w-2.5" v-if="index === 0 && isLive">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span class="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
                </span>
                <span :class="[
                  'text-[10px] font-bold',
                  index === selectedSessionIndex
                    ? 'text-slate-900 dark:text-white'
                    : 'text-slate-500 dark:text-slate-400 group-hover:text-primary'
                ]">#{{ totalCount - ((pageIndex - 1) * pageSize) - index }}</span>
              </div>
            </div>

            <!-- 展开模式 -->
            <div class="expanded-content w-full pl-2 py-4" v-show="sessionNodesExpanded">
              <div class="space-y-6 h-full overflow-y-auto pr-2 pb-4">
                <!-- 标题栏 -->
                <div class="flex items-center justify-between sticky top-0 bg-background-light dark:bg-background-dark/95 z-10 py-2 backdrop-blur-sm">
                  <h3 class="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider pl-1">{{ $t('analyticsSingle.sessionNodes') }}</h3>
                  <div class="flex items-center gap-2">
                    <span class="text-[10px] font-mono text-slate-300">{{ totalCount }} {{ $t('analyticsSingle.records') }}</span>
                    <!-- 收起按钮 -->
                    <button @click="toggleSessionNodes" class="w-6 h-6 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 hover:text-primary transition-colors">
                      <span class="material-symbols-outlined text-[16px]">chevron_left</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- 历史直播会话 -->
              <div v-for="(session, index) in liveSessionData.sessions.slice(0, 5)" :key="`${session.sessionId}-${index}`" @click="selectSession(session, index)" :class="[
                'rounded-2xl p-4 shadow-sm transition-all cursor-pointer group mb-4',
                index === selectedSessionIndex
                  ? 'bg-primary/5 dark:bg-primary/10 border-2 border-primary shadow-primary/20 ring-2 ring-primary/20'
                  : 'bg-white dark:bg-surface-dark border border-slate-100 dark:border-slate-800 hover:shadow-soft hover:border-primary/30'
              ]">
                <div class="flex justify-between items-start mb-2">
                  <span class="text-[10px] font-medium text-slate-400">{{ formatSessionTime(session.sessionId) || '2026-01-30' }}</span>
                  <span class="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-[9px] font-bold px-1.5 py-0.5 rounded-md">{{ formatDuration(session.liveDuration || 7200) }}</span>
                </div>
                  <div class="flex justify-between items-center mb-3">
                    <h4 class="font-bold text-slate-800 dark:text-white text-xs truncate max-w-[210px] group-hover:text-primary transition-colors">{{ (session.title || $t('analyticsSingle.liveSession', { sessionId: session.sessionId })) + ` #${totalCount - ((pageIndex - 1) * pageSize) - index}` }}</h4>
                    <!-- MUSIC标签暂时隐藏 -->
                    <!-- <span class="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[9px] font-bold px-1.5 py-0.5 rounded-md">MUSIC</span> -->
                  </div>
                <div class="grid grid-cols-3 gap-1.5 pt-2 border-t border-slate-50 dark:border-slate-800">
                  <div>
                    <div class="text-[9px] font-semibold text-slate-400 uppercase">{{ $t('analyticsSingle.rev') }}</div>
                    <div class="text-[11px] font-bold text-primary">{{ formatGiftValue(session.totalGiftValue || 0) }}</div>
                  </div>
                  <div>
                    <div class="text-[9px] font-semibold text-slate-400 uppercase">{{ $t('analyticsSingle.avg') }}</div>
                    <div class="text-[11px] font-bold text-slate-700 dark:text-slate-300">{{ formatNumber(session.onlineUsers || 0) }}</div>
                  </div>
                  <div>
                    <div class="text-[9px] font-semibold text-slate-400 uppercase">{{ $t('analyticsSingle.likes') }}</div>
                    <div class="text-[11px] font-bold text-slate-700 dark:text-slate-300">{{ formatNumber(session.likeCount || 0) }}</div>
                  </div>
                </div>
              </div>

              <!-- 分页控件（展开模式） -->
              <div v-if="totalPages > 1 && sessionNodesExpanded" class="w-full mt-6">
                <div class="flex items-center justify-between">
                  <button
                    @click="goToPage(pageIndex - 1)"
                    :disabled="pageIndex <= 1 || isLoadingPage"
                    :class="[
                      'font-bold text-xs py-2.5 px-5 rounded-xl shadow-md transition-all transform',
                      (pageIndex <= 1 || isLoadingPage)
                        ? 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-primaryDark text-white shadow-primary/20 hover:-translate-y-0.5'
                    ]"
                  >
                    {{ $t('analyticsSingle.previousPage') }}
                  </button>

                  <div class="text-text-secondary dark:text-gray-300 text-sm flex items-center">
                    <div v-if="isLoadingPage" class="flex items-center bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-xl">
                      <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-3"></div>
                      <span class="text-primary font-medium">{{ $t('analyticsSingle.loading') }}</span>
                    </div>
                    <div v-else>
                       {{ pageIndex }} / {{ totalPages }}
                       <span class="text-text-secondary dark:text-gray-400 ml-2">({{ totalCount }} {{ $t('analyticsSingle.records') }})</span>
                    </div>
                  </div>

                  <button
                    @click="goToPage(pageIndex + 1)"
                    :disabled="pageIndex >= totalPages || isLoadingPage"
                    :class="[
                      'font-bold text-xs py-2.5 px-5 rounded-xl shadow-md transition-all transform',
                      (pageIndex >= totalPages || isLoadingPage)
                        ? 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 cursor-not-allowed'
                        : 'bg-primary hover:bg-primaryDark text-white shadow-primary/20 hover:-translate-y-0.5'
                    ]"
                  >
                    {{ $t('analyticsSingle.nextPage') }}
                  </button>
                </div>
              </div>
            </div>
            <!-- 分页控件简化版（收起模式） -->
            <div v-if="totalPages > 1 && !sessionNodesExpanded" class="w-full mt-4">
              <div class="flex items-center justify-between">
                <button @click="goToPage(pageIndex - 1)" :disabled="pageIndex <= 1 || isLoadingPage" :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center transition-all ml-2',
                  (pageIndex <= 1 || isLoadingPage)
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primaryDark text-white hover:scale-110'
                ]" :title="$t('analyticsSingle.previousPage')">
                  <span class="material-symbols-outlined text-[16px]">chevron_left</span>
                </button>

                <button @click="goToPage(pageIndex + 1)" :disabled="pageIndex >= totalPages || isLoadingPage" :class="[
                  'w-8 h-8 rounded-full flex items-center justify-center transition-all mr-2',
                  (pageIndex >= totalPages || isLoadingPage)
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primaryDark text-white hover:scale-110'
                ]" :title="$t('analyticsSingle.nextPage')">
                  <span class="material-symbols-outlined text-[16px]">chevron_right</span>
                </button>
              </div>
            </div>
          </div>
          <!-- 主内容区 - 参与度时间线和AI洞察 -->
          <div class="md:col-span-2 space-y-4 md:space-y-6"
            :class="sessionNodesExpanded ? 'xl:col-span-9' : 'xl:col-span-11'">
            <!-- 直播间信息面板 -->
            <div
              class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
              <div class="flex flex-col gap-6">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-3">
                    <h3 class="text-2xl font-bold text-slate-900 dark:text-white">{{ streamerInfo.name || $t('analyticsSingle.liveRoom') }} -
                      {{ $t('analyticsSingle.session') }} {{ previousSelectedState.sessionId || selectedSessionId }}</h3>
                    <span
                      v-if="isCurrentSessionLive"
                      class="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold border border-primary/20">{{ $t('analyticsSingle.onAir') }}</span>
                    <span
                        v-else
                        class="px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold border border-slate-200 dark:border-slate-700">{{ $t('analyticsSingle.completed') }}</span>
                  </div>
                  <div class="flex flex-wrap gap-4 mb-4">
                    <div class="flex items-center gap-2 text-xs">
                      <span class="text-slate-400 font-medium uppercase tracking-wider text-[10px]">{{ $t('analyticsSingle.startTime') }}</span>
                      <span class="font-mono font-semibold text-slate-700 dark:text-slate-200">{{
                        formatSessionTime(previousSelectedState.sessionId) || formatSessionTime(selectedSessionId) }}</span>
                    </div>
                    <div class="flex items-center gap-2 text-xs">
                      <span class="text-slate-400 font-medium uppercase tracking-wider text-[10px]">{{ $t('analyticsSingle.endTime') }}</span>
                      <span class="font-mono font-semibold text-slate-700 dark:text-slate-200">{{
                        formatSessionEndTime(previousSelectedState.sessionId) || formatSessionTime(selectedSessionId) }}</span>
                    </div>
                  </div>
                  <div class="flex gap-3 items-start">
                    <span
                      class="material-symbols-outlined text-primary text-xl mt-0.5 flex-shrink-0 transform rotate-180">format_quote</span>
                    <div class="flex-1">
                      <input class="expand-toggle" :id="'expand-text-toggle-' + selectedSessionId" type="checkbox" />
                      <p
                        class="text-sm text-slate-600 dark:text-slate-300 font-medium leading-relaxed max-w-2xl expand-content">
                        {{ getAnalysisSummary() }}
                      </p>
                      <label
                        v-if="shouldShowReadMore()"
                        class="expand-btn text-xs font-bold text-primary cursor-pointer hover:underline mt-1 inline-block select-none"
                        :for="'expand-text-toggle-' + selectedSessionId">
                        <span class="more">{{ $t('analyticsSingle.readMore') }}</span>
                        <span class="less">{{ $t('analyticsSingle.showLess') }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab切换效果 -->
            <div class="flex items-center gap-4 border-b border-slate-200 dark:border-slate-700">
              <button @click="activeTab = 'broadcast-script'" :class="[
                'relative group px-6 py-3 text-sm font-bold transition-colors border-b-2',
                activeTab === 'broadcast-script'
                  ? 'text-primary dark:text-primary border-primary'
                  : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 border-transparent hover:border-slate-300 dark:hover:border-slate-600'
              ]">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-[18px]">description</span>
                  {{ $t('analyticsSingle.broadcastScript') }}
                </div>
              </button>
              <button @click="activeTab = 'data-details'" :class="[
                'relative group px-6 py-3 text-sm font-bold transition-colors border-b-2',
                activeTab === 'data-details'
                  ? 'text-primary dark:text-primary border-primary'
                  : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 border-transparent hover:border-slate-300 dark:hover:border-slate-600'
              ]">
                <div class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-[18px]">pie_chart</span>
                  {{ $t('analyticsSingle.dataDetails') }}
                </div>
              </button>
            </div>
            
            <!-- Broadcast Script 内容 -->
            <div v-if="activeTab === 'broadcast-script'" class="space-y-6">
              <!-- 直播流程详情加载状态 -->
              <div v-if="transcriptionSummaryLoading" class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
                <div class="flex items-center justify-center py-8">
                  <div class="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mr-3"></div>
                  <span class="text-primary text-sm font-medium">{{ $t('analyticsSingle.loadingTranscriptionSummary') }}</span>
                </div>
              </div>

              <!-- 直播流程详情内容 -->
              <div v-else-if="transcriptionSummaryData" class="space-y-6">
                <!-- 直播主题 -->
                <div v-if="getTranscriptionTheme()" class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
                  <h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span class="material-symbols-outlined text-[16px]">topic</span>
                    {{ $t('analyticsSingle.liveTheme') }}
                  </h4>
                  <div class="bg-primary/5 dark:bg-primary/10 rounded-xl p-4 border border-primary/20">
                    <h3 class="text-lg font-bold text-slate-900 dark:text-white mb-2">{{ getTranscriptionTheme() }}</h3>
                    <div class="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <span class="material-symbols-outlined text-[14px]">info</span>
                      <span>{{ $t('analyticsSingle.aiGeneratedSummary') }}</span>
                    </div>
                  </div>
                </div>

                <!-- 内容段落分析 -->
                <div class="space-y-4">
                  <h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2 px-1">
                    <span class="material-symbols-outlined text-[16px]">list_alt</span>
                    {{ $t('analyticsSingle.contentParagraphAnalysis') }}
                  </h4>

                  <!-- 段落列表 -->
                  <div v-for="(paragraph, index) in getTranscriptionParagraphs()" :key="index"
                       class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800 hover:shadow-md transition-all">
                    <div class="flex justify-between items-center mb-5 pb-4 border-b border-slate-50 dark:border-slate-700/50">
                      <div class="flex items-center gap-4">
                        <span class="flex items-center justify-center w-8 h-8 rounded-lg bg-primaryLight dark:bg-primary/20 text-primaryDark dark:text-primary font-bold text-sm">
                          {{ index + 1 }}
                        </span>
                        <h5 class="font-bold text-slate-900 dark:text-white">{{ paragraph.title }}</h5>
                      </div>
                      <span class="bg-primary text-white text-[11px] font-bold px-2.5 py-1 rounded-lg font-mono tracking-tight shadow-sm">
                        {{ paragraph.timeRange }}
                      </span>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div class="space-y-1.5">
                        <div class="flex items-center gap-2 text-[10px] font-bold text-primary uppercase tracking-wide">
                          <span class="material-symbols-outlined text-[14px]">description</span>
                          {{ $t('analyticsSingle.contentDescription') }}
                        </div>
                        <p class="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{{ paragraph.description }}</p>
                      </div>
                      <div class="space-y-1.5">
                        <div class="flex items-center gap-2 text-[10px] font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wide">
                          <span class="material-symbols-outlined text-[14px]">rate_review</span>
                          {{ $t('analyticsSingle.organizationEvaluation') }}
                        </div>
                        <p class="text-sm text-slate-600 dark:text-slate-400 leading-relaxed italic">{{ paragraph.critique }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 整体总结 -->
                <div v-if="getTranscriptionOverallSummary()" class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
                  <h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    <span class="material-symbols-outlined text-[16px]">summarize</span>
                    {{ $t('analyticsSingle.overallSummary') }}
                  </h4>
                  <div class="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                    <p class="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{{ getTranscriptionOverallSummary() }}</p>
                  </div>
                </div>
              </div>

              <!-- 无数据状态 -->
              <div v-else class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
                <div class="flex flex-col items-center justify-center py-8">
                  <span class="material-symbols-outlined text-4xl text-slate-300 mb-4">description</span>
                  <p class="text-slate-400 text-sm">{{ $t('analyticsSingle.noTranscriptionSummaryData') }}</p>
                </div>
              </div>
            </div>

            <!-- Data Details 内容 -->
            <div v-if="activeTab === 'data-details'" class="space-y-6">
              <!-- Performance Trends 图表区域 -->
              <div class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider">{{ $t('analyticsSingle.performanceTrends') }} - {{ performanceViewMode === 'cumulative' ? $t('analyticsSingle.cumulative') : $t('analyticsSingle.avgPerMinute') }}</h4>
                  <div class="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                    <button @click="switchPerformanceViewMode('cumulative')" :class="[
                      'px-3 py-1.5 text-[10px] font-bold flex items-center gap-1 transition-colors',
                      performanceViewMode === 'cumulative'
                        ? 'text-white bg-primary rounded shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    ]">
                      <span class="material-symbols-outlined text-[14px]">show_chart</span>
                      {{ $t('analyticsSingle.cumulative').toUpperCase() }}
                    </button>
                    <button @click="switchPerformanceViewMode('avg-per-minute')" :class="[
                      'px-3 py-1.5 text-[10px] font-bold flex items-center gap-1 transition-colors',
                      performanceViewMode === 'avg-per-minute'
                        ? 'text-white bg-primary rounded shadow-sm'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    ]">
                      <span class="material-symbols-outlined text-[14px]">timeline</span>
                      {{ $t('analyticsSingle.avgPerMinute').toUpperCase() }}
                    </button>
                  </div>
                </div>
                
                <!-- 分钟级视图的指标选择器 -->
                <div v-if="performanceViewMode === 'avg-per-minute'" class="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8 pl-1">
                  <button @click="selectMinuteMetric('onlineUsers')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedMinuteMetric === 'onlineUsers' || selectedMinuteMetric === 'all'
                        ? 'bg-primary ring-primary/20 group-hover:ring-primary/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedMinuteMetric === 'onlineUsers' || selectedMinuteMetric === 'all'
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.avgOnlineUsersPerMinute') }}</span>
                  </button>
                  <button @click="selectMinuteMetric('revenue')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedMinuteMetric === 'revenue' || selectedMinuteMetric === 'all'
                        ? 'bg-rose-500 ring-rose-500/20 group-hover:ring-rose-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedMinuteMetric === 'revenue' || selectedMinuteMetric === 'all'
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.avgRevenuePerMinute') }}</span>
                  </button>
                  <button @click="selectMinuteMetric('enterUsers')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedMinuteMetric === 'enterUsers' || selectedMinuteMetric === 'all'
                        ? 'bg-blue-500 ring-blue-500/20 group-hover:ring-blue-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedMinuteMetric === 'enterUsers' || selectedMinuteMetric === 'all'
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.avgEnterUsersPerMinute') }}</span>
                  </button>
                  <button @click="selectMinuteMetric('interactionUsers')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedMinuteMetric === 'interactionUsers' || selectedMinuteMetric === 'all'
                        ? 'bg-purple-500 ring-purple-500/20 group-hover:ring-purple-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedMinuteMetric === 'interactionUsers' || selectedMinuteMetric === 'all'
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.avgInteractionUsersPerMinute') }}</span>
                  </button>
                </div>
                
                <!-- 累计视图的指标选择器 -->
                <div v-else class="flex items-center gap-6 mb-8 pl-1">
                  <button @click="toggleCumulativeMetric('likeCount')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedCumulativeMetrics.includes('likeCount')
                        ? 'bg-purple-500 ring-purple-500/20 group-hover:ring-purple-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedCumulativeMetrics.includes('likeCount')
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.likes') }}</span>
                  </button>
                  <button @click="toggleCumulativeMetric('totalInteractions')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedCumulativeMetrics.includes('totalInteractions')
                        ? 'bg-pink-500 ring-pink-500/20 group-hover:ring-pink-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedCumulativeMetrics.includes('totalInteractions')
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.interaction') }}</span>
                  </button>
                  <button @click="toggleCumulativeMetric('totalGiftCount')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedCumulativeMetrics.includes('totalGiftCount')
                        ? 'bg-cyan-500 ring-cyan-500/20 group-hover:ring-cyan-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedCumulativeMetrics.includes('totalGiftCount')
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.giftCount') }}</span>
                  </button>
                  <button @click="toggleCumulativeMetric('totalGiftValue')" class="flex items-center gap-2 group text-left">
                    <div :class="[
                      'w-2.5 h-2.5 rounded-full ring-2 transition-all',
                      selectedCumulativeMetrics.includes('totalGiftValue')
                        ? 'bg-emerald-500 ring-emerald-500/20 group-hover:ring-emerald-500/40'
                        : 'bg-slate-300 dark:bg-slate-600 ring-transparent group-hover:bg-slate-400'
                    ]"></div>
                    <span :class="[
                      'text-[10px] font-bold uppercase tracking-wide',
                      selectedCumulativeMetrics.includes('totalGiftValue')
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200'
                    ]">{{ $t('analyticsSingle.totalValue') }}</span>
                  </button>
                </div>

                <!-- 图表区域 -->
                <div class="h-80 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-100 dark:border-slate-700">
                  <div v-if="chartLoading" class="h-full flex items-center justify-center">
                    <div class="text-center">
                      <div class="loading-spinner mx-auto mb-4"></div>
                      <p class="text-primary text-sm font-medium">{{ $t('analyticsSingle.chartLoading') }}</p>
                    </div>
                  </div>
                  <div v-else-if="performanceViewMode === 'cumulative' && totalStatsData && totalStatsData.length > 0" class="h-full">
                    <!-- CUMULATIVE 模式图表容器 -->
                    <div id="total-stats-chart" class="w-full h-full"></div>
                  </div>
                  <div v-else class="h-full">
                    <!-- ECharts 图表容器 -->
                    <div id="engagement-chart" class="w-full h-full"></div>
                  </div>
                </div>
              </div>

              <!-- Intelligence Highlights 高光时刻 -->
              <!-- 260120 临时隐藏 
              <div class="overflow-hidden">
                <h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 px-1 flex items-center gap-2">
                  <span class="material-symbols-outlined text-[16px]">bolt</span>
                  Intelligence Highlights
                </h4>
                <div class="flex gap-6 overflow-x-auto pb-6 pt-1 px-1 snap-x no-scrollbar">
                  <div class="min-w-[400px] bg-white dark:bg-surface-dark rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden snap-center group cursor-pointer hover:shadow-soft hover:-translate-y-1 transition-all duration-300">
                    <div class="relative h-48 overflow-hidden">
                      <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
                      <img alt="Concert Highlight" class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" src="https://lh3.googleusercontent.com/aida-public/AB6AXuD2cd1rQXln01xxSmuxAgA5gjxly3jvTMg3TTI_yIdwbPKf1E_S85diwLB80LAdkVAhK3bRBRLTc7f82MAz5JP2-2U9h3_GnRlc6U8huBftpakzJVr169n6N--T2oQH_bk6zJEuMqQid2XitWqPtoX7VuPZwFGtRuXaj_15Mb3zFNWq_4uMEwwDndxdJ5fbwz3t4XBSNg1vq1DnFjeRiFzdwReMuX9GTuZpB2LOUC8Y_PIgAKCsq9z_xD20ojR6pwCL87tH4zeZAWEY"/>
                      <span class="absolute top-4 left-4 z-20 bg-slate-900/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg backdrop-blur-sm border border-white/10">00:45</span>
                    </div>
                    <div class="p-5">
                      <div class="flex justify-between items-start mb-3 pb-3 border-b border-slate-50 dark:border-slate-800">
                        <div>
                          <div class="text-[10px] font-bold text-emerald-500 uppercase tracking-wide mb-0.5">Spike</div>
                          <div class="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-1">
                            +4,500
                            <span class="material-symbols-outlined text-[16px] text-emerald-500">trending_up</span>
                          </div>
                        </div>
                        <div class="text-right">
                          <div class="text-[10px] font-bold text-indigo-500 uppercase tracking-wide mb-0.5">Sentiment</div>
                          <div class="text-sm font-bold text-slate-900 dark:text-white">Positive</div>
                        </div>
                      </div>
                      <p class="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Viewer spike during the "Neon Dreams" solo set.</p>
                    </div>
                  </div>
                  
                  <div class="min-w-[400px] bg-white dark:bg-surface-dark rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden snap-center group cursor-pointer hover:shadow-soft hover:-translate-y-1 transition-all duration-300">
                    <div class="relative h-48 overflow-hidden">
                      <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div>
                      <img alt="Crowd Highlight" class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" src="https://lh3.googleusercontent.com/aida-public/AB6AXuDJ0jkjY1enqz_dlQRnnhq3ju5eiTE9T-jsw-2XyEdJEPF9doLOt8u2AQL8s48gpiKd0jL1BpEOGnZYBfUXjgf-SdWYsUgJcs99eGEaroAEI6oFNEXZSEmGd5AlY4qHGJy14SaUkoSsJx6TSukna9YAy7mTvB2_BzNAWXE6n192F9g86XpbGBRe6TZuok4bCtozBVIIvMGEkcYJk5ym1N4fz0icI2FFgdSK1U3BVi82aYBemPNWIGKdd1ihjijEf8rua9sSSKqxY55R"/>
                      <span class="absolute top-4 left-4 z-20 bg-slate-900/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg backdrop-blur-sm border border-white/10">01:22</span>
                    </div>
                    <div class="p-5">
                      <div class="flex justify-between items-start mb-3 pb-3 border-b border-slate-50 dark:border-slate-800">
                        <div>
                          <div class="text-[10px] font-bold text-emerald-500 uppercase tracking-wide mb-0.5">Spike</div>
                          <div class="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-1">
                            +1,200
                            <span class="material-symbols-outlined text-[16px] text-emerald-500">trending_up</span>
                          </div>
                        </div>
                        <div class="text-right">
                          <div class="text-[10px] font-bold text-indigo-500 uppercase tracking-wide mb-0.5">Sentiment</div>
                          <div class="text-sm font-bold text-slate-900 dark:text-white">Excited</div>
                        </div>
                      </div>
                      <p class="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Gifting frenzy triggered by the milestone celebration.</p>
                    </div>
                  </div>
                  
                  <div class="min-w-[400px] bg-white dark:bg-surface-dark rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden snap-center group cursor-pointer hover:shadow-soft hover:-translate-y-1 transition-all duration-300 opacity-60 hover:opacity-100">
                    <div class="relative h-48 bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <span class="material-symbols-outlined text-4xl text-slate-300">image</span>
                      <span class="absolute top-4 left-4 z-20 bg-slate-900/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-lg">02:15</span>
                    </div>
                    <div class="p-5">
                      <div class="flex justify-between items-start mb-3 pb-3 border-b border-slate-50 dark:border-slate-800">
                        <div>
                          <div class="text-[10px] font-bold text-emerald-500 uppercase tracking-wide mb-0.5">Spike</div>
                          <div class="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-1">
                            +890
                            <span class="material-symbols-outlined text-[16px] text-emerald-500">trending_up</span>
                          </div>
                        </div>
                        <div class="text-right">
                          <div class="text-[10px] font-bold text-indigo-500 uppercase tracking-wide mb-0.5">Sentiment</div>
                          <div class="text-sm font-bold text-slate-900 dark:text-white">Shocked</div>
                        </div>
                      </div>
                      <p class="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Surprise guest reveal creates massive chat velocity.</p>
                    </div>
                  </div>
                </div>
              </div>
              -->

              <!-- Gifter Intelligence & Monetization -->
              <div class="bg-white dark:bg-surface-dark rounded-2xl p-6 shadow-soft border border-slate-100 dark:border-slate-800">
                <div class="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-8">
                  <h4 class="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                    <span class="material-symbols-outlined text-[16px]">monetization_on</span>
                    {{ $t('analyticsSingle.gifterIntelligence') }} & {{ $t('analyticsSingle.monetization') }}
                  </h4>
                  <span class="bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold px-3 py-1.5 rounded-full border border-emerald-100 dark:border-emerald-800 flex items-center gap-1 self-start sm:self-auto relative min-w-[120px]">
                    <div v-if="contributionStats.loading" class="absolute inset-0 flex items-center justify-center bg-emerald-50/80 dark:bg-emerald-900/30 rounded-full backdrop-blur-sm">
                      <div class="flex items-center gap-1">
                        <div class="w-3 h-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                        <span class="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">{{ $t('analyticsSingle.loading') }}</span>
                      </div>
                    </div>
                    <div v-show="!contributionStats.loading" class="flex items-center gap-1">
                      <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      {{ $t('analyticsSingle.penetration').toUpperCase() }}: {{ penetrationPercentage }}
                    </div>
                  </span>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <!-- Unique Gifters 卡片 -->
                  <div class="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-700/50 relative min-h-[80px] flex items-center">
                    <div v-if="contributionStats.loading" class="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-800/60 rounded-2xl backdrop-blur-sm">
                      <div class="flex flex-col items-center gap-2">
                        <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span class="text-[10px] font-medium text-primary whitespace-nowrap">{{ $t('analyticsSingle.loading') }}</span>
                      </div>
                    </div>
                    <div class="w-full">
                      <div class="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-wide">{{ $t('analyticsSingle.uniqueGifters') }}</div>
                      <div class="flex items-center gap-3">
                        <div
                          class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                          <span class="material-symbols-outlined text-[20px]">groups</span>
                        </div>
                        <h3 class="text-2xl font-bold text-slate-900 dark:text-white">{{ giftStatsData ? giftStatsData.totalGiftUsers : '-' }}</h3>
                      </div>
                    </div>
                  </div>
                  <!-- Avg / Gifter 卡片 -->
                  <div class="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-700/50 relative min-h-[80px] flex items-center">
                    <div v-if="contributionStats.loading" class="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-800/60 rounded-2xl backdrop-blur-sm">
                      <div class="flex flex-col items-center gap-2">
                        <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span class="text-[10px] font-medium text-primary whitespace-nowrap">{{ $t('analyticsSingle.loading') }}</span>
                      </div>
                    </div>
                    <div class="w-full">
                      <div class="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-wide">{{ $t('analyticsSingle.avgPerGifter') }}</div>
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                          <span class="material-symbols-outlined text-[20px]">account_balance_wallet</span>
                        </div>
                        <span class="text-3xl font-bold text-slate-900 dark:text-white">{{ giftStatsData ? giftStatsData.avgGiftValuePerUser : '-' }}</span>
                      </div>
                    </div>
                  </div>
                  <!-- Avg / Gift 卡片 -->
                  <div class="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-700/50 relative min-h-[80px] flex items-center">
                    <div v-if="contributionStats.loading" class="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-800/60 rounded-2xl backdrop-blur-sm">
                      <div class="flex flex-col items-center gap-2">
                        <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span class="text-[10px] font-medium text-primary whitespace-nowrap">{{ $t('analyticsSingle.loading') }}</span>
                      </div>
                    </div>
                    <div class="w-full">
                      <div class="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-wide">{{ $t('analyticsSingle.avgPerGift') }}</div>
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center">
                          <span class="material-symbols-outlined text-[20px]">card_giftcard</span>
                        </div>
                        <span class="text-3xl font-bold text-slate-900 dark:text-white">{{ giftStatsData ? giftStatsData.avgGiftValuePerGift : '-' }}</span>
                      </div>
                    </div>
                  </div>
                  <!-- Whale Density 卡片 -->
                  <div class="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-100 dark:border-slate-700/50 relative min-h-[80px] flex items-center">
                    <div v-if="contributionStats.loading" class="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-slate-800/60 rounded-2xl backdrop-blur-sm">
                      <div class="flex flex-col items-center gap-2">
                        <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <span class="text-[10px] font-medium text-primary whitespace-nowrap">{{ $t('analyticsSingle.loading') }}</span>
                      </div>
                    </div>
                    <div class="w-full">
                      <div class="text-[10px] font-bold text-slate-400 uppercase mb-2 tracking-wide">{{ $t('analyticsSingle.whaleDensity') }}</div>
                      <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                          <span class="material-symbols-outlined text-[20px]">diamond</span>
                        </div>
                        <span class="text-3xl font-bold text-slate-900 dark:text-white">{{ giftStatsData ? (giftStatsData.whaleDensity || '-') : '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex gap-1 p-1 bg-slate-50 dark:bg-slate-800/50 rounded-xl w-fit mb-6 border border-slate-100 dark:border-slate-700/50">
                  <button 
                    @click="switchGifterTableViewMode('top-spenders')"
                    :class="[
                      'text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all',
                      gifterTableViewMode === 'top-spenders'
                        ? 'bg-primary text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                    ]"
                  >{{ $t('analyticsSingle.topSpenders') }}</button>
                  <button 
                    @click="switchGifterTableViewMode('most-frequent')"
                    :class="[
                      'text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all',
                      gifterTableViewMode === 'most-frequent'
                        ? 'bg-primary text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                    ]"
                  >{{ $t('analyticsSingle.mostFrequent') }}</button>
                  <button 
                    @click="switchGifterTableViewMode('top-gifts')"
                    :class="[
                      'text-xs font-bold px-4 py-2 rounded-lg shadow-sm transition-all',
                      gifterTableViewMode === 'top-gifts'
                        ? 'bg-primary text-white'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                    ]"
                  >{{ $t('analyticsSingle.topGifts') }}</button>
                </div>
                
                <div class="overflow-x-auto">
                  <table class="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr class="text-[10px] text-slate-400 uppercase border-b border-slate-100 dark:border-slate-800">
                        <th class="py-4 pl-4 font-bold w-16">{{ $t('analyticsSingle.rank') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-spenders' || gifterTableViewMode === 'most-frequent'">{{ $t('analyticsSingle.user') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-spenders' || gifterTableViewMode === 'most-frequent'">{{ $t('analyticsSingle.participationCount') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-spenders' || gifterTableViewMode === 'most-frequent'">{{ $t('analyticsSingle.messageCount') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-spenders' || gifterTableViewMode === 'most-frequent'">{{ $t('analyticsSingle.giftCount') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-gifts'">{{ $t('analyticsSingle.giftType') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-gifts'">{{ $t('analyticsSingle.giftCount') }}</th>
                        <th class="py-4 font-bold" v-if="gifterTableViewMode === 'top-gifts'">{{ $t('analyticsSingle.avgValue') }}</th>
                        <th class="py-4 pr-4 font-bold text-right" v-if="gifterTableViewMode === 'top-spenders' || gifterTableViewMode === 'most-frequent'">{{ $t('analyticsSingle.totalValue') }}</th>
                        <th class="py-4 pr-4 font-bold text-right" v-if="gifterTableViewMode === 'top-gifts'">{{ $t('analyticsSingle.totalValue') }}</th>
                      </tr>
                    </thead>
                    <tbody class="text-sm">
                      <!-- TOP SPENDERS 视图加载状态 -->
                      <tr v-if="gifterTableViewMode === 'top-spenders' && userParticipationRankingLoading" class="text-center">
                        <td colspan="8" class="py-8">
                          <div class="flex items-center justify-center gap-2">
                            <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <span class="text-primary text-sm font-medium">{{ $t('analyticsSingle.loadingUserParticipationRanking') }}</span>
                          </div>
                        </td>
                      </tr>
                      
                      <!-- MOST FREQUENT 视图加载状态 -->
                      <tr v-if="gifterTableViewMode === 'most-frequent' && userParticipationRankingLoading" class="text-center">
                        <td colspan="8" class="py-8">
                          <div class="flex items-center justify-center gap-2">
                            <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <span class="text-primary text-sm font-medium">{{ $t('analyticsSingle.loadingUserParticipationRanking') }}</span>
                          </div>
                        </td>
                      </tr>
                      
                      <!-- TOP GIFTS 视图加载状态 -->
                      <tr v-if="gifterTableViewMode === 'top-gifts' && giftTypeRankingLoading" class="text-center">
                        <td colspan="8" class="py-8">
                          <div class="flex items-center justify-center gap-2">
                            <div class="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <span class="text-primary text-sm font-medium">{{ $t('analyticsSingle.loadingGiftTypeRanking') }}</span>
                          </div>
                        </td>
                      </tr>
                      
                      <!-- TOP SPENDERS 视图无数据状态 -->
                      <tr v-else-if="gifterTableViewMode === 'top-spenders' && (!userParticipationRankingData || userParticipationRankingData.length === 0)" class="text-center">
                        <td colspan="8" class="py-8">
                          <div class="flex flex-col items-center justify-center gap-2">
                            <span class="material-symbols-outlined text-3xl text-slate-300">group</span>
                            <span class="text-slate-400 text-sm">{{ $t('analyticsSingle.noUserParticipationData') }}</span>
                          </div>
                        </td>
                      </tr>
                      
                      <!-- MOST FREQUENT 视图无数据状态 -->
                      <tr v-else-if="gifterTableViewMode === 'most-frequent' && (!userParticipationRankingData || userParticipationRankingData.length === 0)" class="text-center">
                        <td colspan="8" class="py-8">
                          <div class="flex flex-col items-center justify-center gap-2">
                            <span class="material-symbols-outlined text-3xl text-slate-300">group</span>
                            <span class="text-slate-400 text-sm">{{ $t('analyticsSingle.noUserParticipationData') }}</span>
                          </div>
                        </td>
                      </tr>
                      
                      <!-- TOP GIFTS 视图无数据状态 -->
                      <tr v-else-if="gifterTableViewMode === 'top-gifts' && (!giftTypeRankingData || giftTypeRankingData.length === 0)" class="text-center">
                        <td colspan="8" class="py-8">
                          <div class="flex flex-col items-center justify-center gap-2">
                            <span class="material-symbols-outlined text-3xl text-slate-300">card_giftcard</span>
                            <span class="text-slate-400 text-sm">{{ $t('analyticsSingle.noGiftTypeRankingData') }}</span>
                          </div>
                        </td>
                      </tr>
                      
                      <!-- TOP SPENDERS 视图数据行 -->
                      <template v-else-if="gifterTableViewMode === 'top-spenders'">
                        <tr v-for="(user, index) in getSortedUserParticipationData('totalGiftValue').slice(0, 10)"
                          :key="user.userInfoId"
                          class="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                          <td class="py-3 pl-4">
                            <div :class="[
                              'w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs shadow-sm',
                              index === 0 ? 'bg-primary text-white shadow-primary/20' :
                                index === 1 ? 'bg-teal-500 text-white' :
                                  index === 2 ? 'bg-teal-500 text-white' :
                                    'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-300'
                            ]">
                              {{ index + 1 }}
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-3">
                              <!-- 用户头像 -->
                              <div class="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white dark:ring-slate-700">
                                <img v-if="user.avatarUrl"
                                     :src="user.avatarUrl"
                                     :alt="user.userName || $t('analyticsSingle.userAvatar')"
                                     class="w-full h-full object-cover"
                                     @error="handleImageError">
                                <div v-else
                                     class="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                  <span class="material-symbols-outlined text-slate-400">person</span>
                                </div>
                              </div>
                              <span class="font-bold text-slate-900 dark:text-white truncate max-w-[150px]">{{
                                user.userName || $t('analyticsSingle.userPrefix', { id: user.userInfoId }) }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-primary">forum</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.participationCount
                                || 0 }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-indigo-500">chat</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.messageCount || 0
                                }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-emerald-500">card_giftcard</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.giftCount || 0
                                }}</span>
                            </div>
                          </td>
                          <td class="py-3 pr-4 text-right">
                            <div class="flex items-center justify-end gap-1">
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.totalGiftValue || 0 }}</span>
                              <span class="material-symbols-outlined text-[14px] text-emerald-500">arrow_upward</span>
                            </div>
                          </td>
                        </tr>
                      </template>
                      
                      <!-- MOST FREQUENT 视图数据行 -->
                      <template v-else-if="gifterTableViewMode === 'most-frequent'">
                        <tr v-for="(user, index) in getSortedUserParticipationData('giftCount').slice(0, 10)"
                          :key="user.userInfoId"
                          class="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                          <td class="py-3 pl-4">
                            <div :class="[
                              'w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs shadow-sm',
                              index === 0 ? 'bg-primary text-white shadow-primary/20' :
                                index === 1 ? 'bg-teal-500 text-white' :
                                  index === 2 ? 'bg-teal-500 text-white' :
                                    'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-300'
                            ]">
                              {{ index + 1 }}
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-3">
                              <!-- 用户头像 -->
                              <div class="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white dark:ring-slate-700">
                                <img v-if="user.avatarUrl"
                                     :src="user.avatarUrl"
                                     :alt="user.userName || $t('analyticsSingle.userAvatar')"
                                     class="w-full h-full object-cover"
                                     @error="handleImageError">
                                <div v-else
                                     class="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                  <span class="material-symbols-outlined text-slate-400">person</span>
                                </div>
                              </div>
                              <span class="font-bold text-slate-900 dark:text-white truncate max-w-[150px]">{{
                                user.userName || $t('analyticsSingle.userPrefix', { id: user.userInfoId }) }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-primary">forum</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.participationCount
                                || 0 }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-indigo-500">chat</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.messageCount || 0
                                }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-emerald-500">card_giftcard</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.giftCount || 0
                                }}</span>
                            </div>
                          </td>
                          <td class="py-3 pr-4 text-right">
                            <div class="flex items-center justify-end gap-1">
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ user.totalGiftValue || 0 }}</span>
                              <span class="material-symbols-outlined text-[14px] text-emerald-500">arrow_upward</span>
                            </div>
                          </td>
                        </tr>
                      </template>
                      
                      <!-- TOP GIFTS 视图数据行 -->
                      <template v-else-if="gifterTableViewMode === 'top-gifts'">
                        <tr v-for="(gift, index) in giftTypeRankingData.slice(0, 10)"
                          :key="gift.giftTypeId || index"
                          class="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                          <td class="py-3 pl-4">
                            <div :class="[
                              'w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs shadow-sm',
                              index === 0 ? 'bg-primary text-white shadow-primary/20' :
                                index === 1 ? 'bg-teal-500 text-white' :
                                  index === 2 ? 'bg-teal-500 text-white' :
                                    'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-300'
                            ]">
                              {{ index + 1 }}
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-3">
                              <!-- 礼物图标 -->
                              <div
                                class="w-9 h-9 rounded-full bg-white flex items-center justify-center ring-2 ring-white dark:ring-slate-700 overflow-hidden">
                                <img v-if="gift.giftImgUrl"
                                    :src="gift.giftImgUrl"
                                    :alt="gift.giftTypeName || $t('analyticsSingle.giftIcon')"
                                    class="w-full h-full object-cover"
                                    @error="handleImageError">
                                <span v-else class="material-symbols-outlined text-emerald-500">card_giftcard</span>
                              </div>
                              <div>
                                <span class="font-bold text-slate-900 dark:text-white truncate max-w-[150px]">{{ gift.giftTypeName || $t('analyticsSingle.giftTypePrefix', { id: gift.giftTypeId }) }}</span>
                                <span class="text-[10px] text-slate-400 block">{{ gift.giftTypeId || $t('analyticsSingle.unknownId') }}</span>
                              </div>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-emerald-500">card_giftcard</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ gift.giftCount || 0 }}</span>
                            </div>
                          </td>
                          <td class="py-3">
                            <div class="flex items-center gap-1">
                              <span class="material-symbols-outlined text-[14px] text-yellow-500">monetization_on</span>
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ gift.avgGiftValue || 0 }}</span>
                            </div>
                          </td>
                          <td class="py-3 pr-4 text-right">
                            <div class="flex items-center justify-end gap-1">
                              <span class="font-mono font-bold text-slate-900 dark:text-white">{{ gift.totalGiftValue || 0 }}</span>
                              <span class="material-symbols-outlined text-[14px] text-emerald-500">arrow_upward</span>
                            </div>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import liveDataService from '@/services/liveDataService'
import * as echarts from 'echarts'
import authService from '@/services/authService'
import { getApiUrl } from '@/config/apiConfig';

export default {
  name: 'Analytics',
    data() {
      return {
        // 组件挂载状态标志
        isComponentMounted: false,
        // 窗口宽度，用于响应式布局
        windowWidth: window.innerWidth,
        loading: false,
        error: null,
        selectedTimeRange: '30d',
        selectedChartTab: 'daily',
        overviewData: {
          totalStreams: 0,
          totalRevenue: 0,
          avgViewers: 0,
          engagementRate: 0
        },
        growthMetrics: {
          daily: 0,
          weekly: 0,
          monthly: 0
        },
        streamData: [],
        liveMetrics: {
          currentViewers: 0,
          viewerChange: 0,
          currentRevenue: 0,
          revenueChange: 0,
          engagementRate: 0,
          engagementChange: 0
        },
        chartLoading: false,
        // 图表数据
        chartData: {
          onlineUsers: [],
          likeCount: [],
          interactions: [],
          xAxis: []
        },
        // ECharts实例
        chartInstance: null,
        // 当前选中的主播ID
        currentStreamerId: null,
        // 直播会话数据
        liveSessions: [],
        selectedSessionId: null,
        selectedSessionIndex: -1,
        // 分页相关数据
        pageIndex: 1,
        pageSize: 5,
        totalCount: 0,
        totalPages: 0,
        // 分页加载状态
        isLoadingPage: false,
        // Session Nodes 展开/收起状态
        sessionNodesExpanded: true,
        // Top Gifters数据
        topGiftersList: [],
        topGiftersLoading: false,
        // 评论列表数据
        commentsList: [],
        commentsLoading: false,
        // 复制提示相关
        copyToast: {
          show: false,
          message: '',
          type: 'success'
        },
        // 主播信息
        streamerInfo: {
          name: '',
          avatar: '',
          socialHandle: '',
          followers: 0
        },
        // 直播会话数据
        liveSessionData: {
          schedule: '',
          sessions: []
        },
        // 打赏贡献分布数据
        contributionStats: {
          loading: false,
          data: [],
          totalUsers: 0
        },
        // 金主排行榜数据
        rewardList: [],
        rewardListLoading: false,
        rewardListLoadingMore: false,
        rewardListPageIndex: 1,
        rewardListPageSize: 10,
        hasMoreRewardData: true,
        // 头像懒加载相关
        avatarObserver: null,
        loadedAvatars: new Set(),
        selectedTimelineTab: 'current-session',
        selectedMetric: 'all',
      // 新增字段：当前激活的tab
      activeTab: 'data-details',
      // 新增字段：选中的场次标题
      selectedSessionTitle: '',
      // 新增字段：选中的场次描述
      selectedSessionDescription: '',
      // 新增字段：场次分析数据
      sessionAnalysisData: null,
      // 新增字段：场次分析加载状态
      sessionAnalysisLoading: false,
      // 新增字段：Performance Trends 视图模式
      performanceViewMode: 'cumulative', // 'cumulative' 或 'avg-per-minute'
      // 新增字段：分钟级统计数据
      minuteStatsData: null,
      // 新增字段：分钟级数据加载状态
      minuteStatsLoading: false,
      // 新增字段：选中的分钟级指标
      selectedMinuteMetric: 'onlineUsers', // 'onlineUsers', 'revenue', 'enterUsers', 'interactionUsers'
      // 新增字段：礼物统计数据
      giftStatsData: null,
      // 新增字段：礼物统计数据加载状态
      giftStatsLoading: false,
      // 新增字段：用户参与频次排行数据
      userParticipationRankingData: null,
      // 新增字段：用户参与频次排行加载状态
      userParticipationRankingLoading: false,
      // 新增字段：礼物类型排行数据
      giftTypeRankingData: null,
      // 新增字段：礼物类型排行加载状态
      giftTypeRankingLoading: false,
      // 新增字段：表格视图模式（TOP SPENDERS 或 MOST FREQUENT）
      gifterTableViewMode: 'top-spenders', // 'top-spenders' 或 'most-frequent'
      // 新增字段：保存之前的选中状态，用于分页后恢复
      previousSelectedState: {
        sessionId: null,
        pageIndex: null,
        startTime: null,
        endTime: null,
        duration: null
      },
      // 新增字段：保存从History传递的sessionId，用于翻页时检查
      historySessionId: null,
      // 新增字段：总统计数据（CUMULATIVE模式）
      totalStatsData: null,
      // 新增字段：总统计数据加载状态
      totalStatsLoading: false,
      // 新增字段：选中的累计指标
      selectedCumulativeMetrics: ['likeCount', 'totalInteractions', 'totalGiftCount', 'totalGiftValue'],
      // 新增字段：渗透率百分比
      penetrationPercentage: '-',
      
      // 新增字段：直播流程详情数据（transcription summary）
      transcriptionSummaryData: null,
      // 新增字段：直播流程详情加载状态
      transcriptionSummaryLoading: false
    }
  },
  computed: {
    timeRanges() {
      return [
        { label: this.$t('analytics.last7Days'), value: '7d' },
        { label: this.$t('analytics.last30Days'), value: '30d' },
        { label: this.$t('analyticsSingle.last90Days'), value: '90d' },
        { label: this.$t('analyticsSingle.ytd'), value: 'ytd' }
      ];
    },
    chartTabs() {
      return [
        { label: this.$t('analyticsSingle.daily'), value: 'daily' },
        { label: this.$t('analyticsSingle.weekly'), value: 'weekly' },
        { label: this.$t('analyticsSingle.monthly'), value: 'monthly' }
      ];
    },
    revenueSources() {
      return [
        { name: this.$t('analyticsSingle.gift'), amount: 0, percentage: 0, color: '#8b5cf6' },
        { name: this.$t('analyticsSingle.subscription'), amount: 0, percentage: 0, color: '#ec4899' },
        { name: this.$t('analyticsSingle.advertisement'), amount: 0, percentage: 0, color: '#06b6d4' },
        { name: this.$t('analyticsSingle.other'), amount: 0, percentage: 0, color: '#6b7280' }
      ];
    },
    timelineTabs() {
      return [
        { label: this.$t('analyticsSingle.currentSession'), value: 'current-session' },
        { label: this.$t('analyticsSingle.daily'), value: 'daily' },
      ];
    },
    metrics() {
      return [
        { label: this.$t('analyticsSingle.allMetrics'), value: 'all' },
        { label: this.$t('analyticsSingle.onlineUsers'), value: 'online-users' },
        { label: this.$t('analyticsSingle.likes'), value: 'likes' },
        { label: this.$t('analyticsSingle.interaction'), value: 'interactions' }
      ];
    },
    // 判断是否正在直播 - 从App.vue获取直播状态
    isLive() {
      // 尝试从App.vue提供的inject获取直播状态
      if (this.$parent && this.$parent.isLive) {
        return this.$parent.isLive;
      }
      
      // 如果无法从父组件获取，尝试从provide/inject获取
      if (this.$options.inject && this.$options.inject.isLive) {
        const injectedIsLive = this.$options.inject.isLive;
        if (typeof injectedIsLive === 'function') {
          return injectedIsLive();
        }
        return injectedIsLive;
      }
      
      // 如果无法获取，使用原来的逻辑作为后备
      if (!this.liveSessions || this.liveSessions.length === 0) {
        return false;
      }

      // 获取最新的直播时段
      const latestSession = this.liveSessions[0];
      if (!latestSession || !latestSession.sessionId) {
        return false;
      }

      // 解析sessionId格式：2025101715 -> 2025-10-17 15:00:00
      const sessionIdStr = String(latestSession.sessionId);
      if (sessionIdStr.length !== 10) {
        return false;
      }

      try {
        const year = parseInt(sessionIdStr.substring(0, 4));
        const month = parseInt(sessionIdStr.substring(4, 6)) - 1;
        const day = parseInt(sessionIdStr.substring(6, 8));
        const hour = parseInt(sessionIdStr.substring(8, 10));

        // 假设直播时长为2小时
        const startTime = new Date(year, month, day, hour, 0, 0);
        const endTime = new Date(startTime.getTime() + 2 * 60 * 60 * 1000);

        const now = new Date();
        const timeDiff = Math.abs(now.getTime() - endTime.getTime());
        return (now >= startTime && now <= endTime) || timeDiff <= 60 * 1000;
      } catch (error) {
        console.warn('解析直播时间失败:', error);
        return false;
      }
    },
    
    // 判断是否有直播会话数据
    hasLiveSessions() {
      return this.liveSessions && this.liveSessions.length > 0;
    },
    
    // 判断当前选中的直播时段是否正在直播
    isCurrentSessionLive() {
      // 如果没有选中任何直播时段，则不显示 "On Air"
      if (!this.previousSelectedState) {
        return false;
      }
      
      try {
        // 首先尝试使用 endTime
        if (this.previousSelectedState.endTime) {
          const endTime = new Date(this.previousSelectedState.endTime);
          const now = new Date();
          
          // 如果当前时间早于结束时间，说明直播还在进行或刚结束（容差60秒）
          const timeDiff = Math.abs(now.getTime() - endTime.getTime());
          return (now <= endTime) || timeDiff <= 60 * 1000;
        }
        
        // 如果没有 endTime，尝试使用 startTime 和 duration 计算结束时间
        if (this.previousSelectedState.startTime && this.previousSelectedState.duration) {
          const startTime = new Date(this.previousSelectedState.startTime);
          const endTime = new Date(startTime.getTime() + (this.previousSelectedState.duration * 1000));
          const now = new Date();
          
          const timeDiff = Math.abs(now.getTime() - endTime.getTime());
          return (now <= endTime) || timeDiff <= 60 * 1000;
        }
        
        // 如果都没有，检查是否是当前正在直播的场次（第一个场次）
        if (this.selectedSessionIndex === 0 && this.isLive) {
          return true;
        }
        
        return false;
      } catch (error) {
        console.warn('判断当前会话是否直播时出错:', error);
        return false;
      }
    }
  },

  watch: {
    // 监听选中的指标变化，更新图表
    selectedMetric(newMetric, oldMetric) {
      if (newMetric !== oldMetric && this.chartInstance) {
        console.log('指标变化，重新渲染图表:', newMetric);
        this.updateChartWithSelectedMetric();
      }
    },

    // 监听选中的时间线标签变化
    selectedTimelineTab(newTab, oldTab) {
      if (newTab !== oldTab) {
        console.log('时间线标签变化，重新加载图表数据:', newTab);
        this.loadChartData();
      }
    },

    // 监听activeTab变化
    activeTab(newTab, oldTab) {
      if (newTab === 'data-details' && newTab !== oldTab) {
        console.log('切换到data-details标签，重新初始化图表');
        // 使用$nextTick确保DOM已经更新
        this.$nextTick(() => {
          setTimeout(() => {
            this.forceInitChart();
          }, 100);
        });
      } else if (newTab === 'broadcast-script' && newTab !== oldTab) {
        console.log('切换到broadcast-script标签，检查是否需要加载直播流程详情');
        // 如果已经有选中的场次，但没有直播流程详情数据，则加载
        if (this.selectedSessionId && !this.transcriptionSummaryData && !this.transcriptionSummaryLoading) {
          console.log('切换到broadcast-script标签，自动加载直播流程详情');
          this.loadTranscriptionSummary();
        }
      }
    }
  },
  methods: {
    /**
     * 获取头像的src - 支持懒加载
     */
    getAvatarSrc(gifter) {
      if (!gifter) return 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60';

      // 如果已经加载过，直接返回真实URL
      const avatarKey = `${gifter.userName}-${gifter.sessionId}`;
      if (this.loadedAvatars.has(avatarKey)) {
        return gifter.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60';
      }

      // 默认返回占位符，等待懒加载
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTYiIGN5PSIxNiIgcj0iMTYiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTEyIDEyQzEzLjc2MTQgMTIgMTUuNzYxNCAxMy43NjE0IDE2IDEyQzE1LjIzODYgMTIgMTMuMjM4NiAxMy4yMzg2IDEyIDEyWk0xNiAyMEMxOC40MTgzIDIwIDIwIDE4LjQxODMgMjAgMTZDMjAgMTMuNTgxNyAxOC40MTgzIDEyIDE2IDEyQzEzLjU4MTcgMTIgMTIgMTMuNTgxNyAxMiAxNkMxMiAxOC40MTgzIDEzLjU4MTcgMjAgMTYgMjBaIiBmaWxsPSIjNkI3MjgwIi8+Cjwvc3ZnPgo=';
    },

    /**
     * 初始化头像懒加载观察器
     */
    initAvatarObserver() {
      // 如果已经存在观察器，先断开
      if (this.avatarObserver) {
        this.avatarObserver.disconnect();
      }

      // 创建Intersection Observer
      this.avatarObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const avatarUrl = img.getAttribute('data-avatar-url');
            const userName = img.alt || 'unknown';

            // 查找对应的gifter数据
            const rewardContainer = img.closest('.reward-item');
            if (rewardContainer) {
              const index = Array.from(rewardContainer.parentElement.children).indexOf(rewardContainer);
              if (index >= 0 && index < this.rewardList.length) {
                const gifter = this.rewardList[index];
                const avatarKey = `${gifter.userName}-${gifter.sessionId}`;

                // 设置真实图片URL
                if (avatarUrl && !this.loadedAvatars.has(avatarKey)) {
                  img.src = avatarUrl;
                  this.loadedAvatars.add(avatarKey);

                  // 停止观察这个元素
                  this.avatarObserver.unobserve(img);
                }
              }
            }
          }
        });
      }, {
        rootMargin: '50px 0px', // 提前50px开始加载
        threshold: 0.1 // 10%可见时触发
      });

      // 观察所有懒加载头像
      this.$nextTick(() => {
        const lazyAvatars = document.querySelectorAll('.lazy-avatar');
        lazyAvatars.forEach(img => {
          this.avatarObserver.observe(img);
        });
      });
    },

    /**
     * 更新头像观察器（当数据变化时）
     */
    updateAvatarObserver() {
      if (this.avatarObserver) {
        // 断开现有观察
        this.avatarObserver.disconnect();

        // 重新观察
        this.$nextTick(() => {
          const lazyAvatars = document.querySelectorAll('.lazy-avatar');
          lazyAvatars.forEach(img => {
            // 检查是否已经加载过
            const avatarUrl = img.getAttribute('data-avatar-url');
            const userName = img.alt || 'unknown';

            // 查找对应的gifter数据来生成key
            const rewardContainer = img.closest('.reward-item');
            if (rewardContainer) {
              const index = Array.from(rewardContainer.parentElement.children).indexOf(rewardContainer);
              if (index >= 0 && index < this.rewardList.length) {
                const gifter = this.rewardList[index];
                const avatarKey = `${gifter.userName}-${gifter.sessionId}`;

                if (this.loadedAvatars.has(avatarKey)) {
                  // 已经加载过，直接设置真实URL
                  img.src = avatarUrl;
                } else {
                  // 还没有加载，添加到观察器
                  this.avatarObserver.observe(img);
                }
              }
            }
          });
        });
      }
    },
    formatNumber(num) {
      if (!num && num !== 0) return '0';
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toLocaleString('zh-CN');
    },
    
    formatSessionTime(sessionId) {
      if (!sessionId) return this.$t('analyticsSingle.unknownTime');
      
      try {
        // 首先检查previousSelectedState中是否有startTime
        if (this.previousSelectedState && 
            this.previousSelectedState.sessionId === sessionId && 
            this.previousSelectedState.startTime) {
          const date = new Date(this.previousSelectedState.startTime);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hour = String(date.getHours()).padStart(2, '0');
          const minute = String(date.getMinutes()).padStart(2, '0');
          return `${year}.${month}.${day} ${hour}:${minute}`;
        }
        
        // 其次尝试在liveSessions中查找对应的session数据
        if (this.liveSessions && this.liveSessions.length > 0) {
          const session = this.liveSessions.find(s => String(s.sessionId) === String(sessionId));
          if (session && session.startTime) {
            // 使用API返回的startTime字段
            const date = new Date(session.startTime);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const minute = String(date.getMinutes()).padStart(2, '0');
            return `${year}.${month}.${day} ${hour}:${minute}`;
          }
        }
        
        // 如果没有找到对应的session数据或没有startTime字段，回退到基于sessionId的格式化
        const sessionIdStr = String(sessionId);
        if (sessionIdStr.length === 10) {
          const year = sessionIdStr.substring(0, 4);
          const month = sessionIdStr.substring(4, 6);
          const day = sessionIdStr.substring(6, 8);
          const hour = sessionIdStr.substring(8, 10);
          return `${year}.${month}.${day} ${hour}:00`;
        } else if (sessionIdStr.length >= 13) {
          const timestamp = parseInt(sessionIdStr.substring(0, 13));
          const date = new Date(timestamp);
          return date.toLocaleDateString('zh-CN') + ' ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
        } else {
          return sessionIdStr.substring(0, 10);
        }
      } catch (error) {
        console.warn('解析sessionId时间失败:', error);
        return String(sessionId).substring(0, 10) || this.$t('analyticsSingle.unknown');
      }
    },

    /**
     * 格式化场次结束时间
     */
    formatSessionEndTime(sessionId) {
      if (!sessionId) return this.$t('analyticsSingle.unknownTime');
      
      try {
        // 首先检查previousSelectedState中是否有endTime或startTime+duration
        if (this.previousSelectedState && 
            this.previousSelectedState.sessionId === sessionId) {
          if (this.previousSelectedState.endTime) {
            // 使用previousSelectedState中的endTime字段
            const date = new Date(this.previousSelectedState.endTime);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const minute = String(date.getMinutes()).padStart(2, '0');
            return `${year}.${month}.${day} ${hour}:${minute}`;
          } else if (this.previousSelectedState.startTime && this.previousSelectedState.duration) {
            // 如果有startTime和duration，计算结束时间
            const startDate = new Date(this.previousSelectedState.startTime);
            const endDate = new Date(startDate.getTime() + (this.previousSelectedState.duration * 1000));
            const year = endDate.getFullYear();
            const month = String(endDate.getMonth() + 1).padStart(2, '0');
            const day = String(endDate.getDate()).padStart(2, '0');
            const hour = String(endDate.getHours()).padStart(2, '0');
            const minute = String(endDate.getMinutes()).padStart(2, '0');
            return `${year}.${month}.${day} ${hour}:${minute}`;
          }
        }
        
        // 其次尝试在liveSessions中查找对应的session数据
        if (this.liveSessions && this.liveSessions.length > 0) {
          const session = this.liveSessions.find(s => String(s.sessionId) === String(sessionId));
          if (session && session.endTime) {
            // 使用API返回的endTime字段
            const date = new Date(session.endTime);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const minute = String(date.getMinutes()).padStart(2, '0');
            return `${year}.${month}.${day} ${hour}:${minute}`;
          } else if (session && session.startTime && session.duration) {
            // 如果有startTime和duration，计算结束时间
            const startDate = new Date(session.startTime);
            const endDate = new Date(startDate.getTime() + (session.duration * 1000));
            const year = endDate.getFullYear();
            const month = String(endDate.getMonth() + 1).padStart(2, '0');
            const day = String(endDate.getDate()).padStart(2, '0');
            const hour = String(endDate.getHours()).padStart(2, '0');
            const minute = String(endDate.getMinutes()).padStart(2, '0');
            return `${year}.${month}.${day} ${hour}:${minute}`;
          }
        }
        
        // 如果没有找到对应的session数据或没有endTime字段，回退到基于sessionId的格式化
        const sessionIdStr = String(sessionId);
        if (sessionIdStr.length === 10) {
          const year = sessionIdStr.substring(0, 4);
          const month = sessionIdStr.substring(4, 6);
          const day = sessionIdStr.substring(6, 8);
          const hour = sessionIdStr.substring(8, 10);
          // 假设直播时长为2小时，计算结束时间
          const endHour = (parseInt(hour) + 2) % 24;
          const endHourStr = endHour.toString().padStart(2, '0');
          return `${year}.${month}.${day} ${endHourStr}:00`;
        } else if (sessionIdStr.length >= 13) {
          const timestamp = parseInt(sessionIdStr.substring(0, 13));
          const date = new Date(timestamp);
          // 假设直播时长为2小时，计算结束时间
          const endDate = new Date(date.getTime() + 2 * 60 * 60 * 1000);
          return endDate.toLocaleDateString('zh-CN') + ' ' + endDate.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
        } else {
          return sessionIdStr.substring(0, 10);
        }
      } catch (error) {
        console.warn('解析sessionId结束时间失败:', error);
        return String(sessionId).substring(0, 10) || this.$t('analyticsSingle.unknown');
      }
    },
    
    selectTimeRange(range) {
      this.selectedTimeRange = range;
      this.loadAnalyticsData();
    },
    
    async loadAnalyticsData() {
      // 移除全局loading状态，让页面框架直接显示
      this.error = null;

      try {
        // 获取用户绑定的直播间roomid - 从localStorage获取
        const roomId = this.getUserRoomId();

        if (!roomId) {
          console.error('未找到用户绑定的直播间，无法加载分析数据');
          this.error = this.$t('analyticsSingle.bindRoomFirst');
          return;
        }

        console.log('获取到用户绑定的直播间roomid:', roomId);
        this.currentStreamerId = roomId;

        // 更新主播信息 - 直播间名称就是roomid
        this.streamerInfo = {
          name: roomId, // 直播间名称就是roomid
          avatar: '/avatar_default.png',
          socialHandle: roomId, // 社交账号也是roomid
          followers: 0
        };

        // 异步查询创作者详细信息（不阻塞页面显示）
        this.queryCreatorInfo(roomId).then(creatorInfo => {
          if (creatorInfo && creatorInfo.success && creatorInfo.data && creatorInfo.data.length > 0) {
            const creatorData = creatorInfo.data[0];
            console.log('获取到创作者详细信息:', creatorData);

            // 更新头像信息
            if (creatorData.avatarUrl) {
              this.streamerInfo.avatar = creatorData.avatarUrl;
              console.log('已更新Analytics页面头像为:', creatorData.avatarUrl);
            }

            // 更新昵称
            if (creatorData.nickname) {
              this.streamerInfo.name = creatorData.nickname;
            }

            // 更新粉丝数 - 从followerCount字段读取
            if (creatorData.followerCount !== undefined && creatorData.followerCount !== null) {
              this.streamerInfo.followers = creatorData.followerCount;
              console.log('已更新Analytics页面粉丝数为:', creatorData.followerCount);
            }
          }
        }).catch(error => {
          console.error('获取创作者信息失败:', error);
        });

        // 异步加载各个数据部分（不阻塞页面显示）
        this.loadAllDataParts(roomId);

      } catch (error) {
        console.error('初始化分析数据失败:', error);
        this.error = this.$t('analyticsSingle.initFailed');
      }
    },

    /**
     * 异步加载所有数据部分
     */
    async loadAllDataParts(roomId) {
      try {
        // 1. 获取直播会话完整摘要
        console.log('开始获取直播会话完整摘要...');
        await this.loadLiveSessionSummary(roomId);
        console.log('直播会话完整摘要获取完成，liveSessionData:', this.liveSessionData);
        console.log('liveSessions:', this.liveSessions);

        // 2. 加载概览数据
        await this.loadOverviewData();

        // 3. 如果loadLiveSessionSummary没有自动加载图表数据，则手动加载
        // 确保图表数据被加载，即使没有直播会话数据
        if (!this.selectedSessionId && this.liveSessions.length > 0) {
          this.selectedSessionId = this.liveSessions[0].sessionId;
          console.log('手动设置selectedSessionId:', this.selectedSessionId);
        }

        // 4. 并行加载主要数据部分（图表、流表现、实时指标、收入分析）
        console.log('开始并行加载主要数据部分...');
        const [chartResult, streamResult, metricsResult, revenueResult] = await Promise.allSettled([
          this.loadChartData(),
          this.loadStreamPerformanceData(),
          this.loadLiveMetrics(),
          this.loadRevenueAnalysis()
        ]);

        // 处理并行加载的结果（记录错误但不中断流程）
        if (chartResult.status === 'rejected') {
          console.error('图表数据加载失败:', chartResult.reason);
        }
        if (streamResult.status === 'rejected') {
          console.error('流表现数据加载失败:', streamResult.reason);
        }
        if (metricsResult.status === 'rejected') {
          console.error('实时指标加载失败:', metricsResult.reason);
        }
        if (revenueResult.status === 'rejected') {
          console.error('收入分析数据加载失败:', revenueResult.reason);
        }

        // 5. 并行加载场次相关数据（Top Gifters、评论、贡献分布、金主排行榜、场次分析、礼物统计、用户参与频次排行）
        console.log('开始并行加载场次相关数据...');
        const [topGiftersResult, commentsResult, contributionResult, rewardResult, sessionAnalysisResult, giftStatsResult, userParticipationResult] = await Promise.allSettled([
          this.loadTopGifters(),
          this.loadComments(),
          this.loadContributionStats(),
          this.loadRewardList(),
          this.loadSessionAnalysis(), // 添加场次分析数据加载
          this.loadGiftStats(), // 添加礼物统计数据加载
          this.loadUserParticipationRanking() // 添加用户参与频次排行数据加载
        ]);

        // 处理并行加载的结果（记录错误但不中断流程）
        if (topGiftersResult.status === 'rejected') {
          console.error('Top Gifters数据加载失败:', topGiftersResult.reason);
        }
        if (commentsResult.status === 'rejected') {
          console.error('评论数据加载失败:', commentsResult.reason);
        }
        if (contributionResult.status === 'rejected') {
          console.error('贡献分布数据加载失败:', contributionResult.reason);
        }
        if (rewardResult.status === 'rejected') {
          console.error('金主排行榜数据加载失败:', rewardResult.reason);
        }
        if (sessionAnalysisResult.status === 'rejected') {
          console.error('场次分析数据加载失败:', sessionAnalysisResult.reason);
        }
        if (giftStatsResult.status === 'rejected') {
          console.error('礼物统计数据加载失败:', giftStatsResult.reason);
        }

        console.log('所有数据加载完成');

      } catch (error) {
        console.error('加载数据部分失败:', error);
      }
    },
    
    // 获取用户绑定的直播间roomid
    getUserRoomId() {
      try {
        // 检查本地存储的直播间ID - 支持多种存储方式
        const bindingInfo = localStorage.getItem('tiktok-binding-info');
        const roomId = localStorage.getItem('tiktok-live-room-id');
        const currentStreamer = localStorage.getItem('currentStreamer');

        console.log('检查直播间信息 - bindingInfo:', bindingInfo, 'roomId:', roomId, 'currentStreamer:', currentStreamer);

        // 如果没有任何直播间信息，返回null
        if (!bindingInfo && !roomId && !currentStreamer) {
          console.log('没有找到任何直播间绑定信息');
          return null;
        }

        // 获取直播间ID - 优先级：bindingInfo > roomId > currentStreamer
        let targetRoomId = null;
        if (bindingInfo) {
          try {
            const binding = JSON.parse(bindingInfo);
            targetRoomId = binding.roomId || binding.bindingSource;
            console.log('从bindingInfo获取直播间ID:', targetRoomId);
          } catch (error) {
            console.error('解析bindingInfo失败:', error);
          }
        } else if (roomId) {
          targetRoomId = roomId;
          console.log('从roomId获取直播间ID:', targetRoomId);
        } else if (currentStreamer) {
          try {
            // 尝试解析currentStreamer，可能是JSON对象或字符串
            const streamerData = JSON.parse(currentStreamer);
            targetRoomId = streamerData.roomId || streamerData.name || currentStreamer;
            console.log('从currentStreamer解析获取直播间ID:', targetRoomId);
          } catch (error) {
            // 如果解析失败，说明currentStreamer本身就是直播间ID字符串
            targetRoomId = currentStreamer;
            console.log('从currentStreamer直接获取直播间ID:', targetRoomId);
          }
        }

        return targetRoomId;
      } catch (error) {
        console.error('获取用户绑定的直播间roomid失败:', error);
        return null;
      }
    },
    
    async loadLiveSessionSummary(streamerId, skipAutoSelect = false) {
      try {
        console.log('调用getLiveSessionStatsList，streamerId:', streamerId, 'skipAutoSelect:', skipAutoSelect);
        const response = await liveDataService.getLiveSessionStatsList(streamerId, this.pageIndex, 5);
        console.log('getLiveSessionStatsList响应:', response);
        
        if (response && response.success && response.data) {
          console.log('API返回数据:', response.data);
          
          // 根据用户提供的API样例，response.data直接是数组
          let sessionsData = response.data;
          console.log('sessions数据:', sessionsData);
          
          // 保存分页信息
          this.totalCount = response.totalCount || 0;
          this.totalPages = response.totalPages || Math.ceil(this.totalCount / this.pageSize);
          
          // 直接使用API返回的数据，不进行去重
          // 按sessionId倒序排序（最新的排在最前面）
          // sessionId格式：2025121413（年月日时），数字越大表示时间越新
          sessionsData.sort((a, b) => {
            const sessionIdA = String(a.sessionId || a.id || '').padStart(10, '0');
            const sessionIdB = String(b.sessionId || b.id || '').padStart(10, '0');

            // 如果sessionId是数字格式，直接比较
            if (/^\d+$/.test(sessionIdA) && /^\d+$/.test(sessionIdB)) {
              return parseInt(sessionIdB) - parseInt(sessionIdA);
            }

            // 否则按字符串倒序
            return sessionIdB.localeCompare(sessionIdA);
          });

          this.liveSessions = sessionsData;
          
          // 更新liveSessionData，用于左栏显示
          this.liveSessionData = {
            schedule: this.$t('analyticsSingle.autoGeneratedSchedule'),
            sessions: this.liveSessions.map(session => ({
              sessionId: session.sessionId,
              title: session.title || this.$t('analyticsSingle.liveSession', { sessionId: session.sessionId }),
              onlineUsers: (session.statsSummary && session.statsSummary.avgOnlineUsers) || 0,
              likeCount: (session.statsSummary && session.statsSummary.maxLikeCount) || 0,
              totalGiftValue: (session.statsSummary && session.statsSummary.totalGiftValue) || 0,
              totalComments: (session.statsSummary && session.statsSummary.totalMessages) || 0,
              liveDuration: session.duration || 0
            }))
          };
          
          console.log('更新后的liveSessionData（已排序）:', this.liveSessionData);
          console.log('更新后的liveSessions（已排序）:', this.liveSessions);
          
        // 检查是否有从History页面传递过来的selectedSessionId
        const savedSessionId = localStorage.getItem('selectedSessionId');
        console.log('从localStorage读取selectedSessionId:', savedSessionId);
        
        // 保存从History传递的sessionId到historySessionId字段
        if (savedSessionId) {
          this.historySessionId = savedSessionId;
          console.log('保存从History传递的sessionId到historySessionId:', this.historySessionId);
          
          // 清除localStorage中的selectedSessionId，避免下次加载时重复使用
          localStorage.removeItem('selectedSessionId');
          console.log('已清除localStorage中的selectedSessionId');
        }
        
        // 只有在不是分页操作且没有跳过自动选择的情况下，才设置选中状态
        if (!skipAutoSelect) {
          let targetSessionId = null;
          let targetSession = null;
          
          // 检查historySessionId是否存在于当前页的直播会话列表中
          if (this.historySessionId && this.liveSessions.length > 0) {
            const foundIndex = this.liveSessions.findIndex(session => {
              const sessionId = session.sessionId || session.id;
              return String(sessionId) === String(this.historySessionId);
            });
            
            if (foundIndex !== -1) {
              targetSession = this.liveSessions[foundIndex];
              targetSessionId = targetSession.sessionId || targetSession.id;
              console.log('找到匹配的sessionId，使用从History传递的sessionId:', targetSessionId, '索引:', foundIndex);
              
              // 设置选中索引为找到的场次的实际索引
              this.selectedSessionIndex = foundIndex;
            } else {
              console.warn('从History传递的sessionId不存在于当前页的直播会话列表中:', this.historySessionId);
              console.log('根据新需求：如果场次不在第一页，不激活任何卡片');
              // 根据新需求：如果场次不在第一页，不激活任何卡片
              // 不设置targetSessionId和selectedSessionIndex，保持为null和-1
              targetSession = null;
              targetSessionId = null;
              this.selectedSessionIndex = -1;
            }
          } else if (this.liveSessions.length > 0) {
            // 如果没有保存的sessionId，使用第一个场次（最新的场次）
            targetSession = this.liveSessions[0];
            targetSessionId = targetSession.sessionId || targetSession.id;
            console.log('没有保存的sessionId，使用最新的sessionId:', targetSessionId);
            this.selectedSessionIndex = 0;
          }
          
          if (targetSessionId) {
            this.selectedSessionId = targetSessionId;
            // 在获取到selectedSessionId时，立即对previousSelectedState赋值，包含完整的session信息
            this.previousSelectedState = {
              sessionId: this.selectedSessionId,
              pageIndex: this.pageIndex,
              startTime: targetSession.startTime || null,
              endTime: targetSession.endTime || null,
              duration: targetSession.duration || null
            };
            console.log('最终选择的sessionId:', this.selectedSessionId, '选中索引:', this.selectedSessionIndex);
            console.log('已更新previousSelectedState:', this.previousSelectedState);

            // 确保Schedule列表能正确显示选中状态
            console.log('目标场次信息:', targetSession);

            // 注意：这里不再调用loadSessionData，因为数据已经在loadAllDataParts中异步加载
          } else {
            // 如果没有直播会话数据，使用默认sessionId加载图表
            console.log('没有直播会话数据，使用默认sessionId加载图表');
            this.selectedSessionId = 'default-session-1';
            this.selectedSessionIndex = -1; // 无选中状态
            // 即使使用默认sessionId，也更新previousSelectedState
            this.previousSelectedState = {
              sessionId: this.selectedSessionId,
              pageIndex: this.pageIndex,
              startTime: null,
              endTime: null,
              duration: null
            };
            await this.loadChartData();
          }
        } else {
          console.log('跳过自动选择，保持当前选中状态不变');
          // 即使skipAutoSelect为true，我们也应该尝试恢复之前的选中状态
          // 使用previousSelectedState来恢复选中状态
          if (this.previousSelectedState && this.previousSelectedState.sessionId && this.liveSessions.length > 0) {
            // 在当前页的数据中查找之前选中的场次
            const foundIndex = this.liveSessions.findIndex(session => 
              String(session.sessionId) === String(this.previousSelectedState.sessionId)
            );
            
            if (foundIndex !== -1) {
              // 如果找到了之前选中的场次，恢复选中状态
              this.selectedSessionId = this.previousSelectedState.sessionId;
              this.selectedSessionIndex = foundIndex;
              console.log('分页后恢复选中状态（在loadLiveSessionSummary中）:', {
                sessionId: this.selectedSessionId,
                index: this.selectedSessionIndex
              });
            } else {
              // 如果没有找到之前选中的场次，这是正常情况（场次在上一页）
              // 不要自动选择任何场次，保持没有选中状态
              this.selectedSessionId = null;
              this.selectedSessionIndex = -1;
              console.log('分页后未找到之前选中的场次，这是正常情况（场次在上一页），保持没有选中状态');
            }
          }
        }
        } else {
          console.warn('API响应格式不正确:', response);
          // 即使API响应不正确，也尝试加载图表数据
          this.selectedSessionId = 'default-session-1';
          await this.loadChartData();
        }
      } catch (error) {
        console.error('加载直播会话摘要失败:', error);
        this.liveSessions = [];
        this.liveSessionData = {
          schedule: '',
          sessions: []
        };
        // 即使出错，也尝试加载图表数据
        this.selectedSessionId = 'default-session-1';
        await this.loadChartData();
      }
    },
    
    async loadOverviewData() {
      try {
        // 这里可以根据实际业务逻辑计算概览数据
        // 例如：从多个会话中汇总数据
        let totalStreams = 0;
        let totalRevenue = 0;
        let totalViewers = 0;
        let totalEngagement = 0;
        
        this.liveSessions.forEach(session => {
          totalStreams++;
          totalRevenue += session.totalGiftValue || 0;
          totalViewers += session.onlineUsers || 0;
          totalEngagement += (session.totalComments || 0) + (session.likeCount || 0);
        });
        
        const avgViewers = totalStreams > 0 ? Math.round(totalViewers / totalStreams) : 0;
        const engagementRate = totalViewers > 0 ? ((totalEngagement / totalViewers) * 100).toFixed(1) : 0;
        
        this.overviewData = {
          totalStreams,
          totalRevenue,
          avgViewers,
          engagementRate
        };
      } catch (error) {
        console.error('加载概览数据失败:', error);
      }
    },
    
    async loadChartData() {
      if (!this.selectedSessionId) {
        console.warn('loadChartData: selectedSessionId为空，跳过图表加载');
        return;
      }
      
      console.log('开始加载图表数据，selectedSessionId:', this.selectedSessionId, 'currentStreamerId:', this.currentStreamerId, 'performanceViewMode:', this.performanceViewMode);
      
      this.chartLoading = true;
      try {
        const roomId = this.currentStreamerId;
        
        if (!roomId) {
          console.error('loadChartData: roomId为空，无法加载图表数据');
          throw new Error('roomId为空');
        }
        
        // 根据performanceViewMode选择不同的数据加载方式
        if (this.performanceViewMode === 'cumulative') {
          // CUMULATIVE模式：使用总统计数据接口
          console.log('CUMULATIVE模式，加载总统计数据');
          await this.loadTotalStatsData();
        } else {
          // AVG/MIN模式：使用原有的分钟级数据接口
          console.log('AVG/MIN模式，加载分钟级统计数据');
          await this.loadMinuteStatsData();
        }
        
      } catch (error) {
        console.error('加载图表数据失败:', error);
        // 使用模拟数据作为错误后备
        if (this.performanceViewMode === 'cumulative') {
          this.useMockTotalStatsData();
        } else {
          this.useMockMinuteStatsData();
        }
      } finally {
        this.chartLoading = false;
        console.log('图表数据加载完成，chartLoading设置为false');
      }
    },
    
    async loadStreamPerformanceData() {
      try {
        // 使用直播会话数据作为流表现数据
        this.streamData = this.liveSessions.map((session, index) => ({
          id: session.sessionId || index + 1,
          date: this.formatSessionTime(session.sessionId),
          duration: this.formatDuration(session.liveDuration || 0),
          viewers: session.onlineUsers || 0,
          revenue: session.totalGiftValue || 0,
          engagement: session.totalComments ? Math.round((session.totalComments / (session.onlineUsers || 1)) * 100) : 0,
          status: this.isLive && index === 0 ? 'Live' : 'Completed'
        }));
      } catch (error) {
        console.error('加载流表现数据失败:', error);
        this.streamData = [];
      }
    },
    
    async loadLiveMetrics() {
      try {
        if (this.liveSessions.length === 0) return;
        
        const latestSession = this.liveSessions[0];
        
        this.liveMetrics = {
          currentViewers: latestSession.onlineUsers || 0,
          viewerChange: 0, // 这里可以根据历史数据计算变化率
          currentRevenue: latestSession.totalGiftValue || 0,
          revenueChange: 0, // 这里可以根据历史数据计算变化率
          engagementRate: latestSession.totalComments ? Math.round((latestSession.totalComments / (latestSession.onlineUsers || 1)) * 100) : 0,
          engagementChange: 0 // 这里可以根据历史数据计算变化率
        };
      } catch (error) {
        console.error('加载实时指标失败:', error);
      }
    },
    
    async loadRevenueAnalysis() {
      try {
        // 这里可以根据实际业务逻辑计算收入分析数据
        // 暂时使用模拟数据
        const totalRevenue = this.overviewData.totalRevenue;
        
        if (totalRevenue > 0) {
          this.revenueSources = [
            { name: '礼物', amount: Math.round(totalRevenue * 0.61), percentage: 61, color: '#8b5cf6' },
            { name: '订阅', amount: Math.round(totalRevenue * 0.22), percentage: 22, color: '#ec4899' },
            { name: '广告', amount: Math.round(totalRevenue * 0.10), percentage: 10, color: '#06b6d4' },
            { name: '其他', amount: Math.round(totalRevenue * 0.07), percentage: 7, color: '#6b7280' }
          ];
          
          // 计算增长指标
          this.growthMetrics = {
            daily: 2.5,
            weekly: 18.2,
            monthly: 45.7
          };
        }
      } catch (error) {
        console.error('加载收入分析数据失败:', error);
      }
    },
    
    formatDuration(seconds) {
      if (!seconds) return '0' + this.$t('analyticsPage.minuteUnit');
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}${this.$t('analyticsPage.hourUnit')} ${minutes}${this.$t('analyticsPage.minuteUnit')}`;
      }
      return `${minutes}${this.$t('analyticsPage.minuteUnit')}`;
    },
    
    /**
     * 初始化默认图表（当没有selectedSessionId时）
     */
    initDefaultChart() {
      console.log('初始化默认图表');
      // 使用更可靠的方式，确保DOM已经渲染
      this.$nextTick(() => {
        setTimeout(() => {
          this.initDefaultChartWithRetry();
        }, 300);
      });
    },
    
    /**
     * 带重试机制的初始化默认图表
     */
    initDefaultChartWithRetry(retryCount = 0) {
      const maxRetries = 10;
      
      const findChartDom = () => {
        const chartDom = document.getElementById('engagement-chart');
        if (!chartDom) {
          console.warn(`初始化默认图表：图表容器元素未找到: #engagement-chart (重试 ${retryCount}/${maxRetries})`);
          return null;
        }
        
        if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
          console.warn(`初始化默认图表：图表容器尺寸为0 (重试 ${retryCount}/${maxRetries})`);
          return null;
        }
        
        return chartDom;
      };
      
      const chartDom = findChartDom();
      if (!chartDom) {
        if (retryCount < maxRetries) {
          console.log(`初始化默认图表：未找到图表容器，等待重试 (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => {
            this.initDefaultChartWithRetry(retryCount + 1);
          }, 300);
          return;
        } else {
          console.error('初始化默认图表：多次尝试后仍无法找到图表容器');
          return;
        }
      }
      
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }
      
      try {
        this.chartInstance = echarts.init(chartDom);
        console.log('初始化默认图表：ECharts实例创建成功');
        
        // 创建一个默认图表配置，显示示例数据
        const defaultOption = this.getChartOption();
        this.chartInstance.setOption(defaultOption, true);
        console.log('初始化默认图表：默认图表设置成功');
      } catch (error) {
        console.error('初始化默认图表失败:', error);
      }
    },
    
    /**
     * 初始化空图表（页面加载时显示）
     * 现在只在loadChartData失败时调用
     */
    initEmptyChart() {
      console.log('初始化空图表（后备方案）');
      // 使用更可靠的方式，确保DOM已经渲染
      this.$nextTick(() => {
        setTimeout(() => {
          this.initEmptyChartWithRetry();
        }, 300);
      });
    },
    
    /**
     * 带重试机制的初始化空图表
     */
    initEmptyChartWithRetry(retryCount = 0) {
      const maxRetries = 10;
      
      const findChartDom = () => {
        const chartDom = document.getElementById('engagement-chart');
        if (!chartDom) {
          console.warn(`初始化空图表：图表容器元素未找到: #engagement-chart (重试 ${retryCount}/${maxRetries})`);
          return null;
        }
        
        if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
          console.warn(`初始化空图表：图表容器尺寸为0 (重试 ${retryCount}/${maxRetries})`);
          return null;
        }
        
        return chartDom;
      };
      
      const chartDom = findChartDom();
      if (!chartDom) {
        if (retryCount < maxRetries) {
          console.log(`初始化空图表：未找到图表容器，等待重试 (${retryCount + 1}/${maxRetries})`);
          setTimeout(() => {
            this.initEmptyChartWithRetry(retryCount + 1);
          }, 300);
          return;
        } else {
          console.error('初始化空图表：多次尝试后仍无法找到图表容器');
          return;
        }
      }
      
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }
      
      try {
        this.chartInstance = echarts.init(chartDom);
        console.log('初始化空图表：ECharts实例创建成功');
        
        // 创建一个简单的空图表配置
        const emptyOption = {
          backgroundColor: 'transparent',
          title: {
            text: '等待数据加载...',
            textStyle: {
              color: '#9ca3af',
              fontSize: 14
            },
            left: 'center',
            top: 'center'
          },
          xAxis: {
            show: false
          },
          yAxis: {
            show: false
          },
          series: []
        };
        
        this.chartInstance.setOption(emptyOption, true);
        console.log('初始化空图表：空图表设置成功');
      } catch (error) {
        console.error('初始化空图表失败:', error);
      }
    },
    
    initChart() {
      console.log('初始化图表，数据:', this.chartData);
      
      // 使用更可靠的方式查找图表容器
      const findChartDom = () => {
        const chartDom = document.getElementById('engagement-chart');
        if (!chartDom) {
          console.warn('图表容器元素未找到: #engagement-chart，等待重试');
          return null;
        }
        
        // 确保容器有尺寸
        if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
          console.warn('图表容器尺寸为0，等待重试');
          return null;
        }
        
        return chartDom;
      };
      
      let chartDom = findChartDom();
      if (!chartDom) {
        // 如果没找到，等待100ms后重试
        setTimeout(() => {
          chartDom = findChartDom();
          if (chartDom) {
            this.initChartWithDom(chartDom);
          } else {
            console.error('多次尝试后仍无法找到图表容器，使用空图表作为后备');
            this.initEmptyChart();
          }
        }, 100);
        return;
      }
      
      this.initChartWithDom(chartDom);
    },
    
    /**
     * 使用找到的DOM元素初始化图表
     */
    initChartWithDom(chartDom) {
      console.log('找到图表容器:', chartDom);
      
      if (this.chartInstance) {
        console.log('销毁现有图表实例');
        this.chartInstance.dispose();
      }
      
      try {
        this.chartInstance = echarts.init(chartDom);
        console.log('ECharts实例创建成功');
        
        const option = this.getChartOption();
        console.log('ECharts配置选项:', option);
        
        this.chartInstance.setOption(option, true);
        console.log('图表更新成功');
        
        // 监听窗口大小变化，自动调整图表大小
        window.addEventListener('resize', () => {
          if (this.chartInstance) {
            this.chartInstance.resize();
          }
        });
        
      } catch (error) {
        console.error('初始化图表失败:', error);
      }
    },
    
    /**
     * 根据选中的指标更新图表
     */
    updateChartWithSelectedMetric() {
      if (!this.chartInstance) {
        console.warn('图表实例不存在，无法更新图表');
        return;
      }
      
      console.log('更新图表，选中的指标:', this.selectedMetric);
      
      try {
        const option = this.getChartOption();
        this.chartInstance.setOption(option, true);
        console.log('图表更新成功');
      } catch (error) {
        console.error('更新图表失败:', error);
      }
    },
    
    /**
     * 获取图表配置 - 与StreamerDetail.vue保持一致
     */
    getChartOption() {
      const isAllMetrics = this.selectedMetric === 'all';
      const hasData = this.chartData.onlineUsers.length > 0;
      
      // 生成时间轴数据 - 修复时间轴与数据对应问题
      let xAxisData = [];
      let onlineUsersData = [];
      let likeCountData = [];
      let interactionsData = [];
      
      if (hasData) {
        // 确保时间轴和所有数据数组长度一致
        const maxLength = Math.max(
          this.chartData.xAxis.length,
          this.chartData.onlineUsers.length,
          this.chartData.likeCount.length,
          this.chartData.interactions.length
        );
        
        // 创建时间戳和数据的配对数组
        const dataPairs = [];
        for (let i = 0; i < maxLength; i++) {
          const time = i < this.chartData.xAxis.length ? this.chartData.xAxis[i] : '';
          const onlineUser = i < this.chartData.onlineUsers.length ? this.chartData.onlineUsers[i] : 0;
          const likeCount = i < this.chartData.likeCount.length ? this.chartData.likeCount[i] : 0;
          const interaction = i < this.chartData.interactions.length ? this.chartData.interactions[i] : 0;
          
          dataPairs.push({
            time,
            onlineUser,
            likeCount,
            interaction,
            timestamp: this.parseTimestampToDate(time)
          });
        }
        
        // 按时间戳排序，确保时间顺序正确
        dataPairs.sort((a, b) => {
          if (a.timestamp && b.timestamp) {
            return a.timestamp.getTime() - b.timestamp.getTime();
          }
          return 0;
        });
        
        // 提取排序后的数据
        xAxisData = dataPairs.map(item => {
          if (!item.time) return '';
          
          try {
            // 尝试解析时间戳
            if (item.timestamp) {
              // 格式化时间为 HH:MM 格式
              const hours = item.timestamp.getHours().toString().padStart(2, '0');
              const minutes = item.timestamp.getMinutes().toString().padStart(2, '0');
              return `${hours}:${minutes}`;
            }
            
            // 如果无法解析为Date对象，尝试从字符串中提取时间部分
            const timeMatch = item.time.match(/(\d{1,2}):(\d{2})/);
            if (timeMatch) {
              return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
            }
            
            // 如果字符串包含完整的时间戳，尝试提取时间部分
            const datetimeMatch = item.time.match(/\d{4}-\d{2}-\d{2} (\d{2}):(\d{2}):\d{2}/);
            if (datetimeMatch) {
              return `${datetimeMatch[1]}:${datetimeMatch[2]}`;
            }
            
            return item.time;
          } catch (error) {
            console.warn('时间格式化失败:', error, '原始时间:', item.time);
            return item.time;
          }
        });
        
        onlineUsersData = dataPairs.map(item => item.onlineUser);
        likeCountData = dataPairs.map(item => item.likeCount);
        interactionsData = dataPairs.map(item => item.interaction);
      } else {
        // 没有数据时使用示例数据
        xAxisData = Array.from({ length: 12 }, (_, i) => `${18 + Math.floor(i/2)}:${i % 2 === 0 ? '00' : '30'}`);
        onlineUsersData = Array.from({ length: 12 }, (_, i) => 
          Math.floor(8000 + Math.sin(i * 0.5) * 2000 + Math.random() * 1000)
        );
        likeCountData = Array.from({ length: 12 }, (_, i) => 
          Math.floor(500 + Math.sin(i * 0.8) * 200 + Math.random() * 100)
        );
        interactionsData = Array.from({ length: 12 }, (_, i) => 
          Math.floor(200 + Math.sin(i * 0.6) * 100 + Math.random() * 50)
        );
      }

      // 构建series数组
      const series = [];
      
      if (isAllMetrics || this.selectedMetric === 'online-users') {
        series.push({
          name: this.$t('analyticsSingle.onlineUsers'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4, // 减小数据点大小
          showSymbol: xAxisData.length <= 20, // 数据点少时才显示符号
          lineStyle: {
            width: 3, // 减小线宽
            color: '#8b5cf6'
          },
          itemStyle: {
            color: '#8b5cf6',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(139, 92, 246, 0.4)' },
              { offset: 1, color: 'rgba(139, 92, 246, 0.1)' }
            ])
          },
          data: onlineUsersData
        });
      }
      
      if (isAllMetrics || this.selectedMetric === 'likes') {
        series.push({
          name: this.$t('analyticsSingle.likes'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4, // 减小数据点大小
          showSymbol: xAxisData.length <= 20, // 数据点少时才显示符号
          lineStyle: {
            width: 3, // 减小线宽
            color: '#ec4899'
          },
          itemStyle: {
            color: '#ec4899',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(236, 72, 153, 0.4)' },
              { offset: 1, color: 'rgba(236, 72, 153, 0.1)' }
            ])
          },
          data: likeCountData
        });
      }
      
      if (isAllMetrics || this.selectedMetric === 'interactions') {
        series.push({
          name: this.$t('analyticsSingle.interaction'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4, // 减小数据点大小
          showSymbol: xAxisData.length <= 20, // 数据点少时才显示符号
          lineStyle: {
            width: 3, // 减小线宽
            color: '#06b6d4'
          },
          itemStyle: {
            color: '#06b6d4',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(6, 182, 212, 0.4)' },
              { offset: 1, color: 'rgba(6, 182, 212, 0.1)' }
            ])
          },
          data: interactionsData
        });
      }

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(42, 34, 57, 0.9)',
          borderColor: 'rgba(129, 140, 248, 0.3)',
          textStyle: {
            color: '#ffffff'
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(139, 92, 246, 0.5)'
            }
          },
          formatter: (params) => {
            // 自定义tooltip显示，确保显示完整的时间戳
            let tooltip = `<div style="font-size: 12px; margin-bottom: 5px;">`;

            // 查找原始时间戳
            const index = params[0].dataIndex;
            if (hasData && index < this.chartData.xAxis.length) {
              const originalTime = this.chartData.xAxis[index];
              tooltip += `<strong>${originalTime}</strong></div>`;
            } else {
              tooltip += `<strong>${xAxisData[index] || '未知时间'}</strong></div>`;
            }

            params.forEach(param => {
              const value = param.value || 0;
              const seriesName = param.seriesName;
              const color = param.color;

              tooltip += `
                <div style="display: flex; align-items: center; margin: 2px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></span>
                  <span style="flex: 1;">${seriesName}:</span>
                  <span style="font-weight: bold; margin-left: 10px;">${this.formatNumber(value)}</span>
                </div>
              `;
            });

            return tooltip;
          }
        },
        legend: {
          data: series.map(s => s.name),
          textStyle: {
            color: '#9ca3af'
          },
          right: 10,
          top: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: {
              color: 'rgba(156, 163, 175, 0.3)'
            }
          },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 11,
            // 动态调整X轴标签显示间隔，避免标签重叠 - 进一步减少标签密度
            interval: (index) => {
              // 使用闭包捕获xAxisData变量
              const totalPoints = xAxisData.length;

              // 根据不同时间线标签设置不同的间隔控制
              if (this.selectedTimelineTab === 'current-session') {
                // 当前场次视图：极激进的间隔控制，避免文字过密
                if (totalPoints <= 10) {
                  return index % 4 === 0; // 数据点少时，每4个显示一个标签
                } else if (totalPoints <= 20) {
                  return index % 6 === 0; // 中等数量，每6个显示一个标签
                } else if (totalPoints <= 40) {
                  return index % 10 === 0; // 数据点较多，每10个显示一个标签
                } else if (totalPoints <= 60) {
                  return index % 15 === 0; // 数据点很多，每15个显示一个标签
                } else if (totalPoints <= 120) {
                  return index % 25 === 0; // 数据点非常多，每25个显示一个标签
                } else if (totalPoints <= 200) {
                  return index % 40 === 0; // 数据点极多，每40个显示一个标签
                } else {
                  return index % 60 === 0; // 数据点超级多，每60个显示一个标签
                }
              } else if (this.selectedTimelineTab === 'daily') {
                // 日视图：激进的间隔控制，避免文字过密
                if (totalPoints <= 12) {
                  return index % 4 === 0; // 数据点少时，每4个显示一个标签
                } else if (totalPoints <= 24) {
                  return index % 8 === 0; // 中等数量，每8个显示一个标签
                } else if (totalPoints <= 48) {
                  return index % 12 === 0; // 数据点较多，每12个显示一个标签
                } else if (totalPoints <= 72) {
                  return index % 18 === 0; // 数据点很多，每18个显示一个标签
                } else if (totalPoints <= 120) {
                  return index % 25 === 0; // 数据点非常多，每25个显示一个标签
                } else if (totalPoints <= 200) {
                  return index % 40 === 0; // 数据点极多，每40个显示一个标签
                } else {
                  return index % 60 === 0; // 数据点超级多，每60个显示一个标签
                }
              } else {
                // 其他视图：保持原有逻辑但进一步减少密度
                if (totalPoints <= 12) {
                  return index % 3 === 0;
                } else if (totalPoints <= 24) {
                  return index % 5 === 0;
                } else if (totalPoints <= 48) {
                  return index % 8 === 0;
                } else if (totalPoints <= 72) {
                  return index % 12 === 0;
                } else {
                  return index % 15 === 0;
                }
              }
            },
            // 旋转标签以避免重叠
            rotate: 45,
            // 确保标签显示完整
            overflow: 'break',
            // 标签之间的最小间隔
            margin: 12
          }
        },
        yAxis: {
          type: 'value',
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 11,
            formatter: (value) => {
              if (value >= 1000) {
                return (value / 1000).toFixed(0) + 'K';
              }
              return value;
            }
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(156, 163, 175, 0.1)',
              type: 'dashed'
            }
          }
        },
        series: series
      };
    },
    
    refreshData() {
      this.loadAnalyticsData();
    },
    
    exportData() {
      // 这里可以实现数据导出逻辑
      alert('导出功能开发中...');
    },
    
    /**
     * 格式化时间戳
     */
    formatTimestamp(timestamp) {
      if (!timestamp) return '';

      try {
        // 如果是ISO字符串格式
        if (timestamp.includes('T')) {
          const date = new Date(timestamp);
          return date.toLocaleString('zh-CN', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        // 如果是其他格式的字符串
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('zh-CN', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        return timestamp;
      } catch (error) {
        console.warn('时间戳格式化失败:', error);
        return timestamp || '';
      }
    },

    /**
     * 格式化本地时间为 YYYY-MM-DD HH:MM:SS 格式（避免时区转换）
     */
    formatLocalDateTime(date) {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return '';
      }

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    /**
     * 图片加载错误处理
     */
    handleImageError(event) {
      event.target.src = '';
    },
    
    /**
     * 加载Top Gifters数据
     */
    async loadTopGifters() {
      if (!this.selectedSessionId) return;
      
      this.topGiftersLoading = true;
      try {
        const roomId = this.currentStreamerId;
        
        // 调用真实的API接口
        const response = await liveDataService.getTopGifters(roomId, this.selectedSessionId);
        
        if (response && response.success && response.data) {
          // 处理API返回的数据，显示10条记录
          this.topGiftersList = response.data.slice(0, 10).map((item, index) => ({
            id: item.id,
            userName: item.userName,
            userId: item.userId,
            avatarUrl: item.avatarUrl,
            coins: item.coins,
            rank: item.rank || (index + 1),
            platform: item.platform
          }));
        } else {
          this.topGiftersList = [];
        }
      } catch (error) {
        console.error('加载Top Gifters失败:', error);
        this.topGiftersList = [];
      } finally {
        this.topGiftersLoading = false;
      }
    },
    
    /**
     * 加载评论列表数据
     */
    async loadComments() {
      if (!this.selectedSessionId) return;
      
      this.commentsLoading = true;
      try {
        const roomId = this.currentStreamerId;
        
        // 调用真实的API接口
        const response = await liveDataService.getLiveComments(
          this.selectedSessionId,
          roomId,
          1, // pageIndex
          10, // pageSize
          'chat' // messageType
        );
        
        if (response && response.success && response.data) {
          // 处理API返回的数据
          this.commentsList = response.data.map((comment, index) => ({
            id: comment.id || `${this.selectedSessionId}-${index}`,
            userName: comment.nickname || comment.userId || 'Unknown User',
            content: comment.content || 'No content',
            messageType: comment.messageType || 'chat',
            timestamp: comment.commentTime || comment.createdAt || 'Unknown time',
            avatar: comment.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60',
            likes: 0,
            replies: 0,
            coins: comment.giftValue || 0,
            giftCount: comment.giftNum || 0,
            giftType: comment.giftType || '',
            giftImgUrl: comment.giftImgUrl || '',
            userLevel: comment.userLevel || 0,
            isSubscribed: comment.isSubscribed || false
          }));
        } else {
          this.commentsList = [];
        }
      } catch (error) {
        console.error('加载评论失败:', error);
        this.commentsList = [];
      } finally {
        this.commentsLoading = false;
      }
    },
    
    /**
     * 格式化礼物价值
     */
    formatGiftValue(value) {
      if (!value) return '0';
      if (value >= 1000000) {
        return (value / 1000000).toFixed(1) + 'M+';
      } else if (value >= 10000) {
        return (value / 1000).toFixed(0) + 'K+';
      } else if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'K+';
      }
      return value.toLocaleString('zh-CN') + '+';
    },
    
    /**
     * 选择场次
     */
    async selectSession(session, index) {
      this.selectedSessionId = session.sessionId;
      this.selectedSessionIndex = index;
      // 从liveSessions中查找完整的session信息
      const fullSession = this.liveSessions.find(s => s.sessionId === session.sessionId);
      this.previousSelectedState = {
        sessionId: this.selectedSessionId,
        pageIndex: this.pageIndex,
        startTime: fullSession ? fullSession.startTime : null,
        endTime: fullSession ? fullSession.endTime : null,
        duration: fullSession ? fullSession.duration : null
      };
      // 更新选中的场次标题和描述
      this.selectedSessionTitle = session.title || this.$t('analyticsSingle.liveSession', { sessionId: session.sessionId });
      // 正式环境下，如果没有描述信息，留空或使用默认提示
      this.selectedSessionDescription = session.description || '';
      
      // 立即显示数值部分的加载中状态
      this.contributionStats.loading = true;
      
      // 立即清除旧的transcription数据并显示加载中状态
      this.transcriptionSummaryData = null;
      this.transcriptionSummaryLoading = true;
      
      // 根据选中的场次更新相关数据
      await this.loadChartData();
      
      // 加载场次分析数据（Prime Time Show板块）
      await this.loadSessionAnalysis();
      
      // 加载Gifter Intelligence相关数据
      await this.loadGiftStats();
      await this.loadUserParticipationRanking();
      await this.loadGiftTypeRanking();
      
      // 重新加载贡献分布数据
      await this.loadContributionStats();
      
      // 加载直播流程详情（transcription summary）
      await this.loadTranscriptionSummary();
    },
    
    /**
     * 加载礼物类型排行数据
     */
    async loadGiftTypeRanking() {
      console.log('========== 开始加载礼物类型排行数据 ==========');
      
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载礼物类型排行：缺少sessionId或roomId');
        console.warn('selectedSessionId:', this.selectedSessionId);
        console.warn('currentStreamerId:', this.currentStreamerId);
        return;
      }

      this.giftTypeRankingLoading = true;
      try {
        console.log('开始加载礼物类型排行，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId,
          pageSize: 20,
          pageIndex: 1
        });

        // 调用liveDataService中的新方法
        console.log('正在调用liveDataService.getGiftTypeRanking...');
        const response = await liveDataService.getGiftTypeRanking(
          this.currentStreamerId,
          this.selectedSessionId,
          20, // pageSize
          1   // pageIndex
        );

        console.log('礼物类型排行响应:', response);
        console.log('响应类型:', typeof response);
        console.log('响应是否成功:', response && response.success);
        console.log('响应数据:', response && response.data);

        if (response && response.success && response.data) {
          this.giftTypeRankingData = response.data;
          console.log('礼物类型排行加载成功，数据长度:', (this.giftTypeRankingData && this.giftTypeRankingData.length) || 0);
          console.log('礼物类型排行数据:', this.giftTypeRankingData);
        } else {
          console.warn('礼物类型排行API返回数据格式不正确:', response);
          this.giftTypeRankingData = null;
        }
      } catch (error) {
        console.error('加载礼物类型排行失败:', error);
        console.error('错误详情:', error.message);
        console.error('错误堆栈:', error.stack);
        this.giftTypeRankingData = null;
      } finally {
        this.giftTypeRankingLoading = false;
        console.log('礼物类型排行加载完成，loading状态:', this.giftTypeRankingLoading);
        console.log('========== 礼物类型排行数据加载结束 ==========');
      }
    },
    
    /**
     * 显示复制提示
     */
    showCopyToast(message) {
      this.copyToast = {
        show: true,
        message: message,
        type: 'success'
      };
      
      // 3秒后自动隐藏
      setTimeout(() => {
        this.copyToast.show = false;
      }, 3000);
    },
    
    /**
     * 处理主播切换事件
     */
    handleStreamerChanged(streamer) {
      console.log('Analytics: 收到主播切换事件:', streamer);

      if (streamer) {
        // 立即清空当前数据，确保响应及时
        this.clearCurrentData();

        // 更新主播信息
        this.streamerInfo = {
          name: streamer.name || '',
          avatar: streamer.avatar || '',
          socialHandle: streamer.roomId || '',
          followers: 0
        };

        // 保存新的直播间ID到localStorage
        if (streamer.roomId) {
          localStorage.setItem('tiktok-live-room-id', streamer.roomId);
          console.log('Analytics: 已更新直播间ID到localStorage:', streamer.roomId);
        }

        // 重新加载分析数据
        this.loadAnalyticsData();
        
        // 如果当前在Broadcast Script标签页，重新加载直播流程详情数据
        if (this.activeTab === 'broadcast-script') {
          console.log('Analytics: 当前在Broadcast Script标签页，准备重新加载直播流程详情');
          // 使用$nextTick确保数据加载完成后再加载直播流程详情
          this.$nextTick(() => {
            setTimeout(() => {
              if (this.selectedSessionId) {
                console.log('Analytics: 重新加载直播流程详情，sessionId:', this.selectedSessionId);
                this.loadTranscriptionSummary();
              }
            }, 1000); // 等待1秒确保分析数据加载完成
          });
        }
      }
    },

    /**
     * 清空当前数据
     */
    clearCurrentData() {
      console.log('Analytics: 清空当前数据');

      // 清空直播会话数据
      this.liveSessions = [];
      this.liveSessionData = {
        schedule: '',
        sessions: []
      };

      // 清空图表数据
      this.chartData = {
        onlineUsers: [],
        likeCount: [],
        interactions: [],
        xAxis: []
      };

      // 清空Top Gifters数据
      this.topGiftersList = [];

      // 清空评论列表数据
      this.commentsList = [];

      // 清空贡献分布数据
      this.contributionStats.data = [];
      this.contributionStats.totalUsers = 0;

      // 清空金主排行榜数据
      this.rewardList = [];
      this.rewardListPageIndex = 1;
      this.hasMoreRewardData = true;

      // 清空概览数据
      this.overviewData = {
        totalStreams: 0,
        totalRevenue: 0,
        avgViewers: 0,
        engagementRate: 0
      };

      // 清空直播指标
      this.liveMetrics = {
        currentViewers: 0,
        viewerChange: 0,
        currentRevenue: 0,
        revenueChange: 0,
        engagementRate: 0,
        engagementChange: 0
      };

      // 清空收入分析数据
      this.revenueSources = [
        { name: '礼物', amount: 0, percentage: 0, color: '#8b5cf6' },
        { name: '订阅', amount: 0, percentage: 0, color: '#ec4899' },
        { name: '广告', amount: 0, percentage: 0, color: '#06b6d4' },
        { name: '其他', amount: 0, percentage: 0, color: '#6b7280' }
      ];

      // 清空流表现数据
      this.streamData = [];

      // 清空分页信息
      this.pageIndex = 1;
      this.totalCount = 0;
      this.totalPages = 0;

      // 清空图表实例
      if (this.chartInstance) {
        this.chartInstance.dispose();
        this.chartInstance = null;
      }

      console.log('Analytics: 数据清空完成');
    },

    /**
     * 查询创作者详细信息
     * @param {string|Array} usernames - 用户名（roomid）列表，可以是字符串（逗号分隔）或数组
     */
    async queryCreatorInfo(usernames) {
      try {
        console.log('开始查询创作者信息，usernames:', usernames);
        
        // 获取token
        let token;
        if (process.env.NODE_ENV === 'development') {
          token = localStorage.getItem('katoken') || authService.getToken();
        } else {
          token = authService.getToken();
        }
        
        if (!token) {
          console.warn('用户未登录，无法查询创作者信息');
          return null;
        }

        // 准备请求头 - 使用特定的app_id和app_secret
        const headers = {
          'req_app_id': 'agent_admin_web',
          'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
          'katoken': token
        };

        // 处理usernames参数
        let usernamesParam;
        if (Array.isArray(usernames)) {
          usernamesParam = usernames.join(',');
        } else {
          usernamesParam = usernames;
        }

        console.log('查询创作者信息请求参数:', {
          usernames: usernamesParam,
          headers: {
            'req_app_id': headers['req_app_id'],
            'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
            'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
          }
        });

        // 构建URL，将参数作为查询字符串
        const apiUrl = `${getApiUrl('CREATOR_QUERY')}?usernames=${encodeURIComponent(usernamesParam)}`;

        // 发送GET请求
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: headers
        });

        console.log('创作者信息查询响应状态:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`查询创作者信息失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('创作者信息查询结果:', result);
        
        return result;
        
      } catch (error) {
        console.error('查询创作者信息失败:', error);
        return null;
      }
    },
    
    /**
     * 加载打赏贡献分布数据
     */
    async loadContributionStats() {
      if (!this.selectedSessionId) return;

      this.contributionStats.loading = true;
      try {
        const roomId = this.currentStreamerId;

        // 调用API获取贡献分布数据（两个段位）
        const response = await liveDataService.getContributionStatsTwoSegments(roomId, this.selectedSessionId);

        if (response && response.success && response.data) {
          // 处理API返回的数据 - 根据用户提供的样例数据格式
          const data = response.data;
          console.log('贡献分布API返回数据:', data);

          // 将API数据转换为前端需要的格式
          this.contributionStats.data = data.map(item => ({
            name: item.contributionSegment || '未知分段',
            percentage: parseFloat((item.userPercent && item.userPercent.replace('%', '')) || 0),
            amount: item.userNum || 0
          }));
          this.contributionStats.totalUsers = data.reduce((sum, item) => sum + (item.userNum || 0), 0);

          // 计算渗透率
          this.calculatePenetration(data);
        } else {
          console.log('贡献分布API返回空数据或失败:', response);
          // 如果没有数据，使用模拟数据
          this.contributionStats.data = [
            { name: '前20%', percentage: 0.42, amount: 6 },
            { name: '20%~40%', percentage: 0.64, amount: 9 },
            { name: '40%~60%', percentage: 1.13, amount: 16 },
            { name: '60%~80%', percentage: 3.75, amount: 53 },
            { name: '后20%', percentage: 94.06, amount: 1330 }
          ];
          this.contributionStats.totalUsers = 1414;

          // 计算渗透率
          this.calculatePenetration(this.contributionStats.data.map(item => ({
            contributionSegment: item.name,
            userNum: item.amount,
            userPercent: item.percentage + '%'
          })));
        }
      } catch (error) {
        console.error('加载贡献分布数据失败:', error);
        // 使用模拟数据作为错误后备
        this.contributionStats.data = [
          { name: '前20%', percentage: 0.42, amount: 6 },
          { name: '20%~40%', percentage: 0.64, amount: 9 },
          { name: '40%~60%', percentage: 1.13, amount: 16 },
          { name: '60%~80%', percentage: 3.75, amount: 53 },
          { name: '后20%', percentage: 94.06, amount: 1330 }
        ];
        this.contributionStats.totalUsers = 1414;

        // 计算渗透率
        this.calculatePenetration(this.contributionStats.data.map(item => ({
          contributionSegment: item.name,
          userNum: item.amount,
          userPercent: item.percentage + '%'
        })));
      } finally {
        this.contributionStats.loading = false;
      }
    },

    /**
     * 计算渗透率
     */
    calculatePenetration(data) {
      if (!data || data.length === 0) {
        this.penetrationPercentage = '0.00%';
        return;
      }

      // 排序段位，从贡献高到低
      const sortedData = data.sort((a, b) => {
        if (a.contributionSegment.includes('20%') && (a.contributionSegment.includes('后') || a.contributionSegment.includes('Bottom'))) return 1;
        if (b.contributionSegment.includes('20%') && (b.contributionSegment.includes('后') || b.contributionSegment.includes('Bottom'))) return -1;
        const aMatch = a.contributionSegment.match(/(\d+)%/);
        const bMatch = b.contributionSegment.match(/(\d+)%/);
        if (aMatch && bMatch) {
          return parseInt(bMatch[1]) - parseInt(aMatch[1]);
        }
        return 0;
      });

      // 使用贡献最高的段位的 userPercent 作为渗透率
      this.penetrationPercentage = sortedData[0] ? (sortedData[0].userPercent || '0.00%') : '0.00%';
      console.log('计算的渗透率:', this.penetrationPercentage);
    },
    
    /**
     * 解析时间戳字符串为Date对象
     */
    parseTimestampToDate(timestamp) {
      if (!timestamp) return null;
      
      try {
        // 尝试解析ISO格式
        if (timestamp.includes('T')) {
          return new Date(timestamp);
        }
        
        // 尝试解析"YYYY-MM-DD HH:MM:SS"格式
        const match = timestamp.match(/(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
        if (match) {
          const [, year, month, day, hour, minute, second] = match;
          return new Date(year, month - 1, day, hour, minute, second);
        }
        
        // 尝试解析"HH:MM"格式
        const timeMatch = timestamp.match(/(\d{1,2}):(\d{2})/);
        if (timeMatch) {
          const [, hour, minute] = timeMatch;
          const now = new Date();
          return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0);
        }
        
        // 尝试直接解析
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return date;
        }
        
        return null;
      } catch (error) {
        console.warn('解析时间戳失败:', error, '原始时间戳:', timestamp);
        return null;
      }
    },
    
    /**
     * 强制初始化图表（带重试机制）
     */
    forceInitChart() {
      console.log('强制初始化图表...');
      
      // 检查组件是否仍然挂载
      if (!this.isComponentMounted) {
        console.log('组件已卸载，停止图表初始化');
        return;
      }
      
      const findChartDom = () => {
        const chartDom = document.getElementById('engagement-chart');
        if (!chartDom) {
          console.warn('强制初始化图表：图表容器元素未找到: #engagement-chart');
          return null;
        }
        
        // 确保容器有尺寸
        if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
          console.warn('强制初始化图表：图表容器尺寸为0');
          return null;
        }
        
        return chartDom;
      };
      
      const chartDom = findChartDom();
      if (!chartDom) {
        console.warn('强制初始化图表：未找到图表容器，等待重试...');
        // 等待200ms后重试，但检查组件是否仍然挂载
        setTimeout(() => {
          if (this.isComponentMounted) {
            this.forceInitChart();
          } else {
            console.log('组件已卸载，停止重试');
          }
        }, 200);
        return;
      }

      // 销毁现有实例
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }

      try {
        // 创建新的图表实例
        this.chartInstance = echarts.init(chartDom);
        const option = this.getChartOption();

        console.log('强制初始化图表：设置选项', option);
        this.chartInstance.setOption(option, true);

        // 监听窗口大小变化
        window.addEventListener('resize', () => {
          if (this.chartInstance) {
            this.chartInstance.resize();
          }
        });

        console.log('强制初始化图表：成功完成');
      } catch (error) {
        console.error('强制初始化图表失败：', error);

        // 如果失败，稍后重试，但检查组件是否仍然挂载
        setTimeout(() => {
          if (this.isComponentMounted) {
            this.forceInitChart();
          } else {
            console.log('组件已卸载，停止重试');
          }
        }, 500);
      }
    },
    
    /**
     * 获取贡献分布颜色
     */
    getContributionColor(index) {
      const colors = [
        '#8b5cf6', // 紫色
        '#ec4899', // 粉色
        '#06b6d4', // 青色
        '#10b981', // 绿色
        '#f59e0b'  // 黄色
      ];
      return colors[index % colors.length];
    },
    
    /**
     * 获取贡献分布偏移量
     */
    getContributionOffset(index) {
      if (index === 0) return 0;
      
      let offset = 0;
      for (let i = 0; i < index; i++) {
        if (this.contributionStats.data[i]) {
          offset += this.contributionStats.data[i].percentage * 2.51;
        }
      }
      return offset;
    },
    
    /**
     * 获取顶部贡献百分比
     */
    getTopContributionPercentage() {
      if (this.contributionStats.data.length === 0) return 0;
      return this.contributionStats.data[0].percentage;
    },
    
    /**
     * 加载金主排行榜数据
     */
    async loadRewardList() {
      if (!this.selectedSessionId) return;
      
      this.rewardListLoading = true;
      // 重置分页状态
      this.rewardListPageIndex = 1;
      // 强制设置为true以便测试无限加载功能
      this.hasMoreRewardData = true;

      try {
        const params = {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId,
          pageSize: this.rewardListPageSize,
          pageIndex: this.rewardListPageIndex
        };

        // 调用真实的API接口
        const response = await liveDataService.getRewardList(params.liveRoomId, params.sessionId, params.pageSize, params.pageIndex);

        if (response && response.success && response.data) {
          // 处理API返回的数据，添加排名信息和头像
          this.rewardList = response.data.map((item, index) => ({
            merchantSn: item.merchantSn,
            liveRoomId: item.liveRoomId,
            sessionId: item.sessionId,
            userName: item.userName,
            coins: item.coins,
            rowNum: item.rowNum || (index + 1),
            avatarUrl: item.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
          }));
        } else {
          // 如果API调用失败，使用默认数据
          this.rewardList = [
            {
              merchantSn: "243636276",
              liveRoomId: params.liveRoomId,
              sessionId: params.sessionId,
              userName: "🐰nao1129🐱☪️",
              coins: 945,
              rowNum: 1,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              merchantSn: "243636276",
              liveRoomId: params.liveRoomId,
              sessionId: params.sessionId,
              userName: "ふる",
              coins: 813,
              rowNum: 2,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              merchantSn: "243636276",
              liveRoomId: params.liveRoomId,
              sessionId: params.sessionId,
              userName: "サイエン",
              coins: 279,
              rowNum: 3,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              merchantSn: "243636276",
              liveRoomId: params.liveRoomId,
              sessionId: params.sessionId,
              userName: "ひで",
              coins: 113,
              rowNum: 4,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            },
            {
              merchantSn: "243636276",
              liveRoomId: params.liveRoomId,
              sessionId: params.sessionId,
              userName: "黒👿",
              coins: 85,
              rowNum: 5,
              avatarUrl: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
            }
          ];
        }
      } catch (error) {
        console.error('加载金主排行榜失败:', error);
        this.rewardList = [];
      } finally {
        this.rewardListLoading = false;
        // 数据加载完成后更新头像观察器
        this.$nextTick(() => {
          this.updateAvatarObserver();
        });
      }
    },

    /**
     * 加载更多金主数据
     */
    async loadMoreRewardData() {
      // 检查是否正在加载或已达到上限
      if (this.rewardListLoadingMore || !this.hasMoreRewardData || this.rewardList.length >= 100) {
        return;
      }

      this.rewardListLoadingMore = true;
      try {
        this.rewardListPageIndex++;
        const roomId = this.currentStreamerId;
        
        const response = await liveDataService.getRewardList(
          roomId, 
          this.selectedSessionId, 
          this.rewardListPageSize, 
          this.rewardListPageIndex
        );
        
        if (response && response.success && response.data && response.data.length > 0) {
          // 合并新数据
          const newData = response.data.map((item, index) => ({
            merchantSn: item.merchantSn,
            liveRoomId: item.liveRoomId,
            sessionId: item.sessionId,
            userName: item.userName,
            coins: item.coins,
            rowNum: item.rowNum || (this.rewardList.length + index + 1),
            avatarUrl: item.avatarUrl || 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-1.2.1&auto=format&fit=crop&w=50&q=60'
          }));
          
          this.rewardList = [...this.rewardList, ...newData];
          
          // 检查是否还有更多数据
          this.hasMoreRewardData = response.data.length >= this.rewardListPageSize && this.rewardList.length < 100;
        } else {
          this.hasMoreRewardData = false;
        }
      } catch (error) {
        console.error('加载更多金主数据失败:', error);
        this.hasMoreRewardData = false;
      } finally {
        this.rewardListLoadingMore = false;
      }
    },

    /**
     * 处理金主列表容器滚动事件（无限加载）
     */
    async handleRewardListScroll() {
      const rewardContainer = document.getElementById('reward-list-container');
      if (!rewardContainer) return;

      // 检查容器是否可滚动
      const isScrollable = rewardContainer.scrollHeight > rewardContainer.clientHeight;
      if (!isScrollable) {
        // 如果容器不可滚动，检查是否还有更多数据
        if (this.hasMoreRewardData && !this.rewardListLoadingMore) {
          console.log('金主列表容器不可滚动，直接加载更多数据');
          await this.loadMoreRewardData();
        }
        return;
      }

      // 计算滚动位置
      const scrollTop = rewardContainer.scrollTop;
      const scrollHeight = rewardContainer.scrollHeight;
      const clientHeight = rewardContainer.clientHeight;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      console.log('金主列表容器滚动检测:', {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollBottom,
        rewardListLoadingMore: this.rewardListLoadingMore,
        hasMoreRewardData: this.hasMoreRewardData,
        rewardListLength: this.rewardList.length
      });

      // 当滚动到底部50px范围内时触发加载
      if (scrollBottom <= 50 && !this.rewardListLoadingMore && this.hasMoreRewardData) {
        console.log('触发金主列表数据加载，距离底部:', scrollBottom);
        await this.loadMoreRewardData();
      }
    },

    /**
     * 处理页面滚动事件（金主列表无限加载）
     */
    async handleWindowScroll() {
      // 获取金主列表容器的位置
      const rewardContainer = document.getElementById('reward-list-container');
      if (!rewardContainer) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const containerBottom = rewardContainer.offsetTop + rewardContainer.offsetHeight;
      const scrollTrigger = containerBottom - scrollTop - windowHeight;

      console.log('页面滚动事件触发 - 金主列表容器检测:', {
        scrollTop,
        windowHeight,
        containerBottom,
        scrollTrigger,
        rewardListLoadingMore: this.rewardListLoadingMore,
        hasMoreRewardData: this.hasMoreRewardData,
        rewardListLength: this.rewardList.length
      });

      // 当金主列表容器距离页面底部还有200px时触发加载
      if (scrollTrigger <= 200 && !this.rewardListLoadingMore && this.hasMoreRewardData) {
        console.log('触发金主列表数据加载，容器底部距离页面底部:', scrollTrigger);
        await this.loadMoreRewardData();
      }
    },

    /**
     * 复制文本到剪贴板
     */
    async copyToClipboard(text) {
      console.log('复制方法被调用，原始文本:', text);

      try {
        await navigator.clipboard.writeText(text);
        console.log('剪贴板成功，显示提示');
        // 显示复制成功提示
        this.showCopyToast(this.$t('analyticsSingle.copySuccess'));
      } catch (error) {
        console.error('剪贴板API失败，回退方案:', error);
        // 备用方案：使用 document.execCommand
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log('回退方案成功，显示提示');
        this.showCopyToast(this.$t('analyticsSingle.copySuccess'));
      }
    },

    /**
     * 跳转到指定页面
     */
    async goToPage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }

      // 设置分页加载状态，防止重复点击
      this.isLoadingPage = true;

      // 保存之前的选中状态和页面信息
      // 只有当有选中状态时才保存，避免保存null状态
      if (this.selectedSessionId) {
        // 从liveSessions中查找完整的session信息
        const fullSession = this.liveSessions.find(s => s.sessionId === this.selectedSessionId);
        this.previousSelectedState = {
          sessionId: this.selectedSessionId,
          pageIndex: this.pageIndex,
          startTime: fullSession ? fullSession.startTime : null,
          endTime: fullSession ? fullSession.endTime : null,
          duration: fullSession ? fullSession.duration : null
        };
        console.log('保存之前的选中状态:', this.previousSelectedState);
      } else {
        console.log('当前没有选中状态，不保存previousSelectedState');
      }

      this.pageIndex = page;
      console.log('跳转到第', page, '页');

      try {
        // 重新加载直播会话数据，传递 skipAutoSelect = true 避免自动设置选中状态
        await this.loadLiveSessionSummary(this.currentStreamerId, true);
        
        // 分页后，检查historySessionId是否存在于当前页
        if (this.historySessionId && this.liveSessions.length > 0) {
          const foundIndex = this.liveSessions.findIndex(session => {
            const sessionId = session.sessionId || session.id;
            return String(sessionId) === String(this.historySessionId);
          });
          
          if (foundIndex !== -1) {
            // 如果从History传递的sessionId存在于当前页，自动激活该场次
            this.selectedSessionId = this.historySessionId;
            this.selectedSessionIndex = foundIndex;
            console.log('分页后找到从History传递的sessionId，自动激活场次:', {
              sessionId: this.selectedSessionId,
              index: this.selectedSessionIndex
            });
            
            // 加载该场次的图表数据
            await this.loadChartData();
          } else {
            // 如果没有找到historySessionId，尝试恢复之前的选中状态
            if (this.previousSelectedState && this.previousSelectedState.sessionId) {
              // 在当前页的数据中查找之前选中的场次
              const foundPreviousIndex = this.liveSessions.findIndex(session => 
                String(session.sessionId) === String(this.previousSelectedState.sessionId)
              );
              
              if (foundPreviousIndex !== -1) {
                // 如果找到了之前选中的场次，恢复选中状态
                this.selectedSessionId = this.previousSelectedState.sessionId;
                this.selectedSessionIndex = foundPreviousIndex;
                console.log('分页后恢复之前的选中状态:', {
                  sessionId: this.selectedSessionId,
                  index: this.selectedSessionIndex
                });
              } else {
                // 如果没有找到之前选中的场次，这是正常情况（场次在上一页）
                // 不要自动选择任何场次，保持没有选中状态
                this.selectedSessionId = null;
                this.selectedSessionIndex = -1;
                console.log('分页后未找到任何匹配的场次，保持没有选中状态');
              }
            } else {
              console.log('没有之前的选中状态需要恢复');
            }
          }
        } else {
          // 如果没有historySessionId，尝试恢复之前的选中状态
          if (this.previousSelectedState && this.previousSelectedState.sessionId) {
            // 在当前页的数据中查找之前选中的场次
            const foundIndex = this.liveSessions.findIndex(session => 
              String(session.sessionId) === String(this.previousSelectedState.sessionId)
            );
            
            if (foundIndex !== -1) {
              // 如果找到了之前选中的场次，恢复选中状态
              this.selectedSessionId = this.previousSelectedState.sessionId;
              this.selectedSessionIndex = foundIndex;
              console.log('分页后恢复选中状态:', {
                sessionId: this.selectedSessionId,
                index: this.selectedSessionIndex
              });
            } else {
              // 如果没有找到之前选中的场次，这是正常情况（场次在上一页）
              // 不要自动选择任何场次，保持没有选中状态
              this.selectedSessionId = null;
              this.selectedSessionIndex = -1;
              console.log('分页后未找到之前选中的场次，这是正常情况（场次在上一页），保持没有选中状态');
            }
          } else {
            console.log('没有之前的选中状态需要恢复');
          }
        }
      } finally {
        // 无论成功还是失败，都恢复按钮状态
        this.isLoadingPage = false;
      }
    },

    /**
     * 返回上一页
     */
    goBack() {
      console.log('返回上一页');
      this.$router.go(-1);
    },

    /**
     * 切换Session Nodes的展开/收起状态
     */
    toggleSessionNodes() {
      this.sessionNodesExpanded = !this.sessionNodesExpanded;
      console.log('Session Nodes状态切换为:', this.sessionNodesExpanded ? '展开' : '收起');
    },

    /**
     * 处理窗口大小变化事件
     */
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
      console.log('窗口大小变化，当前宽度:', this.windowWidth);

      // 如果图表实例存在，重新调整大小
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    },

    /**
     * 获取场次分析数据
     */
    async loadSessionAnalysis() {

      console.log('当前参数:', {
        selectedSessionId: this.selectedSessionId,
        currentStreamerId: this.currentStreamerId
      });
      
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载场次分析数据：缺少sessionId或roomId');
        console.warn('selectedSessionId:', this.selectedSessionId);
        console.warn('currentStreamerId:', this.currentStreamerId);
        return;
      }

      this.sessionAnalysisLoading = true;
      try {
        // 获取token
        let token;
        if (process.env.NODE_ENV === 'development') {
          token = localStorage.getItem('katoken') || authService.getToken();
        } else {
          token = authService.getToken();
        }
        
        if (!token) {
          console.warn('用户未登录，无法获取场次分析数据');
          this.sessionAnalysisData = null;
          return;
        }

        // 准备请求头
        const headers = {
          'req_app_id': 'agent_admin_web',
          'req_app_secret': 'G3e4DQonaj06txSCgs2XyMfWIUOz9vNw',
          'katoken': token
        };

        // 构建URL - 使用新的LIVE_SESSION_ANALYSIS接口配置
        const apiUrl = `${getApiUrl('LIVE_SESSION_ANALYSIS')}/${encodeURIComponent(this.currentStreamerId)}/analysis?sessionId=${encodeURIComponent(this.selectedSessionId)}`;

        console.log('请求场次分析数据，URL:', apiUrl);
        console.log('请求头:', {
          'req_app_id': headers['req_app_id'],
          'req_app_secret': headers['req_app_secret'] ? '***' + headers['req_app_secret'].slice(-4) : 'null',
          'katoken': headers['katoken'] ? '***' + headers['katoken'].slice(-8) : 'null'
        });

        console.log('正在发送API请求...');
        // 发送GET请求
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: headers
        });

        console.log('场次分析数据响应状态:', response.status, response.statusText);

        if (!response.ok) {
          throw new Error(`获取场次分析数据失败: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        console.log('场次分析数据结果:', result);
        
        if (result && result.success && result.data) {
          this.sessionAnalysisData = result.data;
          console.log('场次分析数据加载成功:', result.data);
          
          // 更新选中的场次标题和描述
          if (result.data.title) {
            this.selectedSessionTitle = result.data.title;
            console.log('已更新场次标题:', result.data.title);
          }
          
          // 如果有描述字段，可以更新描述
          // 注意：API返回的数据中没有description字段，所以保持原有的描述
        } else {
          this.sessionAnalysisData = null;
          console.warn('场次分析API返回数据格式不正确:', result);
        }
      } catch (error) {
        console.error('获取场次分析数据失败:', error);
        this.sessionAnalysisData = null;
      } finally {
        this.sessionAnalysisLoading = false;
        console.log('场次分析数据加载完成，loading状态:', this.sessionAnalysisLoading);
        console.log('========== 场次分析数据加载结束 ==========');
      }
    },

    /**
     * 获取场次分析摘要
     */
    getAnalysisSummary() {
      if (!this.sessionAnalysisData || !this.sessionAnalysisData.analysisText) {
        // 正式环境下，如果没有获取到分析数据，返回空字符串或提示信息
        return this.$t('analyticsSingle.dataBeingOrganized');
      }

      try {
        // 解析analysisText JSON字符串
        let analysisText = this.sessionAnalysisData.analysisText;

        // 清理JSON字符串，移除可能的格式问题
        analysisText = analysisText.trim();

        // 尝试修复常见的JSON格式问题
        if (analysisText.includes('，')) {
          // 将中文逗号替换为英文逗号
          analysisText = analysisText.replace(/，/g, ',');
        }

        // 修复多余的逗号问题（数组或对象最后一个元素后的逗号）
        // 使用正则表达式移除多余的逗号
        analysisText = analysisText
          // 移除数组中最后一个元素后的逗号
          .replace(/,(\s*\])/g, '$1')
          // 移除对象中最后一个属性后的逗号
          .replace(/,(\s*\})/g, '$1');

        // 如果JSON以数组开头但不是有效的JSON，尝试包装为对象
        if (analysisText.startsWith('[') && !analysisText.includes('{')) {
          analysisText = `{"data": ${analysisText}}`;
        }

        // console.log('清理后的JSON:', analysisText);

        // 尝试解析清理后的JSON
        let analysis = JSON.parse(analysisText);

        // 如果解析结果是数组，尝试取第一个元素
        if (Array.isArray(analysis) && analysis.length > 0) {
          analysis = analysis[0];
        }

        // 构建摘要文本
        let summary = '';

        // 添加阶段判断
        if (analysis.stageJudgment) {
          summary += `${this.$t('analyticsSingle.stageJudgment')}: ${analysis.stageJudgment}\n`;
        }

        // 添加行为结构
        if (analysis.behaviorStructure) {
          summary += `${this.$t('analyticsSingle.behaviorStructure')}: ${analysis.behaviorStructure}\n`;
        }

        // 添加观众参与度
        if (analysis.audienceParticipation) {
          summary += `${this.$t('analyticsSingle.audienceParticipation')}: ${analysis.audienceParticipation}\n`;
        }

        // 添加营收结构
        if (analysis.revenueStructure) {
          summary += `${this.$t('analyticsSingle.revenueStructure')}: ${analysis.revenueStructure}\n`;
        }

        // 添加亮点
        if (analysis.highlights && Array.isArray(analysis.highlights) && analysis.highlights.length > 0) {
          summary += `${this.$t('analyticsSingle.highlights')}:\n`;
          analysis.highlights.forEach((highlight, index) => {
            summary += `  ${index + 1}. ${highlight}\n`;
          });
        }

        // 添加建议
        if (analysis.suggestions && Array.isArray(analysis.suggestions) && analysis.suggestions.length > 0) {
          summary += `${this.$t('analyticsSingle.suggestions')}:\n`;
          analysis.suggestions.forEach((suggestion, index) => {
            summary += `  ${index + 1}. ${suggestion}\n`;
          });
        }

        // 如果没有构建出任何内容，返回默认提示
        if (!summary.trim()) {
          return this.$t('analyticsSingle.noAnalysisData');
        }

        return summary.trim();
      } catch (error) {
        console.error('解析analysisText失败:', error);
        console.error('原始analysisText:', this.sessionAnalysisData.analysisText);
        // 解析失败时返回提示信息
        return this.$t('analyticsSingle.dataParsingError');
      }
    },

    /**
     * 切换Performance Trends视图模式
     */
    async switchPerformanceViewMode(mode) {
      if (this.performanceViewMode === mode) return;

      // 设置图表加载状态
      this.chartLoading = true;

      try {
        this.performanceViewMode = mode;

        if (mode === 'avg-per-minute') {
          // 切换到分钟级视图，加载分钟级数据
          await this.loadMinuteStatsData();
        } else {
          // 切换到累计视图，加载总统计数据
          await this.loadTotalStatsData();
        }
      } catch (error) {
        console.error('切换视图模式失败:', error);
      } finally {
        // 确保图表加载状态被清除
        this.chartLoading = false;
      }
    },

    /**
     * 加载总统计数据（CUMULATIVE模式）
     */
    async loadTotalStatsData() {
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载总统计数据：缺少sessionId或roomId');
        return;
      }

      this.totalStatsLoading = true;
      try {
        console.log('开始加载总统计数据，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId
        });

        // 调用liveDataService中的新方法
        const response = await liveDataService.getLiveSessionTotalStats(
          this.currentStreamerId,
          this.selectedSessionId
        );

        console.log('总统计数据响应:', response);

        if (response && response.success && response.data) {
          this.totalStatsData = response.data;
          console.log('总统计数据加载成功:', this.totalStatsData);

          // 渲染总统计图表
          this.renderTotalStatsChart();
        } else {
          console.warn('总统计数据API返回数据格式不正确:', response);
          this.totalStatsData = null;
          // 使用模拟数据作为后备
          this.useMockTotalStatsData();
        }
      } catch (error) {
        console.error('加载总统计数据失败:', error);
        this.totalStatsData = null;
        // 使用模拟数据作为后备
        this.useMockTotalStatsData();
      } finally {
        this.totalStatsLoading = false;
      }
    },


    /**
     * 使用模拟总统计数据作为后备
     */
    useMockTotalStatsData() {
      // 生成模拟的总统计数据
      const mockData = [];

      // 生成40个时间点的数据（大约10分钟的数据，每15秒一个点）
      for (let i = 0; i < 40; i++) {
        const calculatedAt = new Date(Date.now() - (40 - i) * 15 * 1000).toISOString().replace('T', ' ').substring(0, 19);

        // 模拟数据：随着时间逐渐增加
        const likeCount = Math.floor(i * 50 + Math.random() * 100);
        const totalInteractions = Math.floor(i * 15 + Math.random() * 30);
        const totalGiftCount = Math.floor(i * 2 + Math.random() * 5);
        const totalGiftValue = Math.floor(i * 100 + Math.random() * 200);

        mockData.push({
          sessionId: this.selectedSessionId || 2026011323,
          liveRoomId: this.currentStreamerId || "1tb.boiz",
          likeCount: likeCount,
          totalInteractions: totalInteractions,
          totalGiftCount: totalGiftCount,
          totalGiftValue: totalGiftValue,
          calculatedAt: calculatedAt
        });
      }

      this.totalStatsData = mockData;
      console.log('使用模拟总统计数据:', mockData);

      // 渲染总统计图表
      this.renderTotalStatsChart();
    },

    /**
     * 渲染总统计图表
     */
    renderTotalStatsChart() {
      if (!this.totalStatsData || !Array.isArray(this.totalStatsData) || this.totalStatsData.length === 0) {
        console.warn('没有总统计数据可渲染');
        return;
      }

      // 使用$nextTick确保DOM已经更新
      this.$nextTick(() => {
        setTimeout(() => {
          this.initTotalStatsChart();
        }, 100);
      });
    },

    /**
     * 初始化总统计图表
     */
    initTotalStatsChart() {
      console.log('初始化总统计图表...');

      const findChartDom = () => {
        const chartDom = document.getElementById('total-stats-chart');
        if (!chartDom) {
          console.warn('总统计图表：图表容器元素未找到: #total-stats-chart');
          return null;
        }

        if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
          console.warn('总统计图表：图表容器尺寸为0');
          return null;
        }

        return chartDom;
      };

      const chartDom = findChartDom();
      if (!chartDom) {
        console.warn('总统计图表：未找到图表容器，等待重试...');
        setTimeout(() => {
          this.initTotalStatsChart();
        }, 200);
        return;
      }

      // 销毁现有实例
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }

      try {
        // 创建新的图表实例
        this.chartInstance = echarts.init(chartDom);
        const option = this.getTotalStatsChartOption();

        console.log('总统计图表：设置选项');
        this.chartInstance.setOption(option, true);

        // 监听窗口大小变化
        window.addEventListener('resize', () => {
          if (this.chartInstance) {
            this.chartInstance.resize();
          }
        });

        console.log('总统计图表：成功完成');
      } catch (error) {
        console.error('初始化总统计图表失败：', error);
        setTimeout(() => {
          this.initTotalStatsChart();
        }, 500);
      }
    },

    /**
     * 获取总统计图表配置
     */
    getTotalStatsChartOption() {
      if (!this.totalStatsData || !Array.isArray(this.totalStatsData) || this.totalStatsData.length === 0) {
        return {
          backgroundColor: 'transparent',
          title: {
            text: this.$t('analyticsSingle.noTotalStatsData'),
            textStyle: {
              color: '#9ca3af',
              fontSize: 14
            },
            left: 'center',
            top: 'center'
          },
          xAxis: {
            show: false
          },
          yAxis: {
            show: false
          },
          series: []
        };
      }

      // 按时间排序数据
      const sortedData = [...this.totalStatsData].sort((a, b) => {
        return new Date(a.calculatedAt) - new Date(b.calculatedAt);
      });

      // 准备数据
      const xAxisData = sortedData.map(item => {
        const date = new Date(item.calculatedAt);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${hours}:${minutes}`;
      });

      const likeCountData = sortedData.map(item => item.likeCount);
      const totalInteractionsData = sortedData.map(item => item.totalInteractions);
      const totalGiftCountData = sortedData.map(item => item.totalGiftCount);
      const totalGiftValueData = sortedData.map(item => item.totalGiftValue);

      // 创建四个数据系列，但只包含选中的指标
      const series = [];
      
      // 点赞数系列
      if (this.selectedCumulativeMetrics.includes('likeCount')) {
        series.push({
          name: this.$t('analyticsSingle.likes'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#8b5cf6' // 紫色
          },
          itemStyle: {
            color: '#8b5cf6',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(139, 92, 246, 0.4)' },
              { offset: 1, color: 'rgba(139, 92, 246, 0.1)' }
            ])
          },
          data: likeCountData
        });
      }
      
      // 互动数系列
      if (this.selectedCumulativeMetrics.includes('totalInteractions')) {
        series.push({
          name: this.$t('analyticsSingle.interaction'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#ec4899' // 粉色
          },
          itemStyle: {
            color: '#ec4899',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(236, 72, 153, 0.4)' },
              { offset: 1, color: 'rgba(236, 72, 153, 0.1)' }
            ])
          },
          data: totalInteractionsData
        });
      }
      
      // 礼物数量系列
      if (this.selectedCumulativeMetrics.includes('totalGiftCount')) {
        series.push({
          name: this.$t('analyticsSingle.giftCount'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#06b6d4' // 青色
          },
          itemStyle: {
            color: '#06b6d4',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(6, 182, 212, 0.4)' },
              { offset: 1, color: 'rgba(6, 182, 212, 0.1)' }
            ])
          },
          data: totalGiftCountData,
          yAxisIndex: 1 // 使用第二个Y轴
        });
      }
      
      // 礼物价值系列
      if (this.selectedCumulativeMetrics.includes('totalGiftValue')) {
        series.push({
          name: this.$t('analyticsSingle.totalValue'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#10b981' // 绿色
          },
          itemStyle: {
            color: '#10b981',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(16, 185, 129, 0.4)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }
            ])
          },
          data: totalGiftValueData,
          yAxisIndex: 1 // 使用第二个Y轴
        });
      }

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(42, 34, 57, 0.9)',
          borderColor: 'rgba(129, 140, 248, 0.3)',
          textStyle: {
            color: '#ffffff'
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(139, 92, 246, 0.5)'
            }
          },
          formatter: (params) => {
            let tooltip = `<div style="font-size: 12px; margin-bottom: 5px;">`;
            const index = params[0].dataIndex;

            // 显示时间
            if (sortedData[index] && sortedData[index].calculatedAt) {
              const date = new Date(sortedData[index].calculatedAt);
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              const seconds = date.getSeconds().toString().padStart(2, '0');
              tooltip += `<strong>${hours}:${minutes}:${seconds}</strong></div>`;
            } else {
              tooltip += `<strong>${this.$t('analyticsSingle.point', { index: index + 1 })}</strong></div>`;
            }

            params.forEach(param => {
              const value = param.value || 0;
              const seriesName = param.seriesName;
              const color = param.color;

              tooltip += `
                <div style="display: flex; align-items: center; margin: 2px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></span>
                  <span style="flex: 1;">${seriesName}:</span>
                  <span style="font-weight: bold; margin-left: 10px;">${this.formatNumber(value)}</span>
                </div>
              `;
            });

            return tooltip;
          }
        },
        legend: {
          data: series.map(s => s.name),
          textStyle: {
            color: '#9ca3af'
          },
          right: 10,
          top: 0,
          type: 'scroll' // 如果图例太多，可以滚动
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: {
              color: 'rgba(156, 163, 175, 0.3)'
            }
          },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 11,
            // 控制X轴标签显示间隔，避免重叠
            interval: (index) => {
              const totalPoints = xAxisData.length;
              if (totalPoints <= 30) {
                return index % 5 === 0; // 每5个显示一个标签
              } else if (totalPoints <= 60) {
                return index % 10 === 0; // 每10个显示一个标签
              } else if (totalPoints <= 120) {
                return index % 20 === 0; // 每20个显示一个标签
              } else {
                return index % 30 === 0; // 每30个显示一个标签
              }
            },
            rotate: 45,
            overflow: 'break',
            margin: 12
          }
        },
        yAxis: [
          {
            type: 'value',
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#9ca3af',
              fontSize: 11,
              formatter: (value) => {
                if (value >= 1000) {
                  return (value / 1000).toFixed(0) + 'K';
                }
                return value;
              }
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(156, 163, 175, 0.1)',
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#9ca3af',
              fontSize: 11,
              formatter: (value) => {
                if (value >= 1000) {
                  return (value / 1000).toFixed(0) + 'K';
                }
                return value;
              }
            },
            splitLine: {
              show: false
            },
            position: 'right'
          }
        ],
        series: series
      };
    },

    /**
     * 加载分钟级统计数据
     */
    async loadMinuteStatsData() {
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载分钟级数据：缺少sessionId或roomId');
        return;
      }

      this.minuteStatsLoading = true;
      try {
        console.log('开始加载分钟级统计数据，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId
        });

        // 调用liveDataService中的新方法
        const response = await liveDataService.getLiveSessionMinuteStats(
          this.currentStreamerId,
          this.selectedSessionId
        );

        console.log('分钟级统计数据响应:', response);

        if (response && response.success && response.data) {
          // 转换API数据格式以匹配我们的代码期望
          const apiData = response.data;
          console.log('原始API数据:', apiData);
          
          // 转换数据格式
          const transformedData = {
            sessionId: apiData.sessionId,
            liveRoomId: apiData.liveRoomId,
            avgOnlineUsersPerMinute: apiData.avgOnlineUsersPerMinute,
            avgRevenuePerMinute: apiData.avgRevenuePerMinute,
            avgEnterUsersPerMinute: apiData.avgEnterUsersPerMinute,
            avgInteractionUsersPerMinute: apiData.avgInteractionUsersPerMinute,
            minuteStats: []
          };
          
          // 转换minuteDetails为minuteStats
          if (apiData.minuteDetails && Array.isArray(apiData.minuteDetails)) {
            // 按时间排序，确保数据按时间顺序排列
            const sortedDetails = [...apiData.minuteDetails].sort((a, b) => {
              const timeA = new Date(a.minute).getTime();
              const timeB = new Date(b.minute).getTime();
              return timeA - timeB;
            });
            
            // 转换为minuteStats格式，使用分钟序号而不是时间字符串
            transformedData.minuteStats = sortedDetails.map((detail, index) => ({
              minute: index + 1, // 使用分钟序号
              onlineUsers: detail.onlineUsers || 0,
              revenue: detail.revenue || 0,
              enterUsers: detail.enterUsers || 0,
              interactionUsers: detail.interactionUsers || 0,
              originalMinute: detail.minute // 保留原始时间用于显示
            }));
            
            console.log('转换后的分钟级数据:', transformedData);
          }
          
          this.minuteStatsData = transformedData;
          console.log('分钟级数据加载成功:', this.minuteStatsData);
          
          // 渲染分钟级图表
          this.renderMinuteStatsChart();
        } else {
          console.warn('分钟级统计数据API返回数据格式不正确:', response);
          this.minuteStatsData = null;
          // 使用模拟数据作为后备
          this.useMockMinuteStatsData();
        }
      } catch (error) {
        console.error('加载分钟级统计数据失败:', error);
        this.minuteStatsData = null;
        // 使用模拟数据作为后备
        this.useMockMinuteStatsData();
      } finally {
        this.minuteStatsLoading = false;
      }
    },

    /**
     * 使用模拟分钟级数据作为后备
     */
    useMockMinuteStatsData() {
      // 生成模拟的分钟级数据
      const mockData = {
        minuteStats: []
      };

      // 生成180分钟的数据（3小时直播）
      for (let i = 0; i < 180; i++) {
        const minute = i + 1;
        // 模拟数据：在线用户数在5000-25000之间波动
        const onlineUsers = Math.floor(5000 + Math.sin(i * 0.1) * 10000 + Math.random() * 3000);
        // 模拟数据：收入在0-500之间波动
        const revenue = Math.floor(Math.abs(Math.sin(i * 0.15) * 300 + Math.random() * 200));
        // 模拟数据：进入用户在0-100之间波动
        const enterUsers = Math.floor(Math.abs(Math.sin(i * 0.2) * 50 + Math.random() * 50));
        // 模拟数据：互动用户在0-50之间波动
        const interactionUsers = Math.floor(Math.abs(Math.sin(i * 0.25) * 25 + Math.random() * 25));

        mockData.minuteStats.push({
          minute: minute,
          onlineUsers: onlineUsers,
          revenue: revenue,
          enterUsers: enterUsers,
          interactionUsers: interactionUsers
        });
      }

      this.minuteStatsData = mockData;
      console.log('使用模拟分钟级数据:', mockData);
      
      // 渲染分钟级图表
      this.renderMinuteStatsChart();
    },

    /**
     * 渲染分钟级统计图表
     */
    renderMinuteStatsChart() {
      if (!this.minuteStatsData || !this.minuteStatsData.minuteStats) {
        console.warn('没有分钟级数据可渲染');
        return;
      }

      // 使用$nextTick确保DOM已经更新
      this.$nextTick(() => {
        setTimeout(() => {
          this.initMinuteStatsChart();
        }, 100);
      });
    },

    /**
     * 初始化分钟级统计图表
     */
    initMinuteStatsChart() {
      console.log('初始化分钟级统计图表...');
      
      const findChartDom = () => {
        const chartDom = document.getElementById('engagement-chart');
        if (!chartDom) {
          console.warn('分钟级图表：图表容器元素未找到: #engagement-chart');
          return null;
        }
        
        if (chartDom.clientWidth === 0 || chartDom.clientHeight === 0) {
          console.warn('分钟级图表：图表容器尺寸为0');
          return null;
        }
        
        return chartDom;
      };
      
      const chartDom = findChartDom();
      if (!chartDom) {
        console.warn('分钟级图表：未找到图表容器，等待重试...');
        setTimeout(() => {
          this.initMinuteStatsChart();
        }, 200);
        return;
      }

      // 销毁现有实例
      if (this.chartInstance) {
        this.chartInstance.dispose();
      }

      try {
        // 创建新的图表实例
        this.chartInstance = echarts.init(chartDom);
        const option = this.getMinuteStatsChartOption();

        console.log('分钟级图表：设置选项');
        this.chartInstance.setOption(option, true);

        // 监听窗口大小变化
        window.addEventListener('resize', () => {
          if (this.chartInstance) {
            this.chartInstance.resize();
          }
        });

        console.log('分钟级图表：成功完成');
      } catch (error) {
        console.error('初始化分钟级图表失败：', error);
        setTimeout(() => {
          this.initMinuteStatsChart();
        }, 500);
      }
    },

    /**
     * 获取分钟级统计图表配置
     */
    getMinuteStatsChartOption() {
      if (!this.minuteStatsData || !this.minuteStatsData.minuteStats) {
        return {
          backgroundColor: 'transparent',
          title: {
            text: this.$t('analyticsSingle.noMinuteStatsData'),
            textStyle: {
              color: '#9ca3af',
              fontSize: 14
            },
            left: 'center',
            top: 'center'
          },
          xAxis: {
            show: false
          },
          yAxis: {
            show: false
          },
          series: []
        };
      }

      const minuteStats = this.minuteStatsData.minuteStats;
      
      // 准备数据 - 使用原始时间中的小时和分钟作为X轴标签
      const xAxisData = minuteStats.map(item => {
        if (item.originalMinute) {
          // 从"2026-01-18 18:29"格式中提取"18:29"，只显示小时和分钟
          const timeStr = item.originalMinute;
          const timeMatch = timeStr.match(/(\d{2}:\d{2})/);
          if (timeMatch) {
            return timeMatch[0];
          } else {
            // 如果没有匹配到HH:MM格式，尝试其他格式
            const altMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
            if (altMatch) {
              const hour = altMatch[1].padStart(2, '0');
              const minute = altMatch[2];
              return `${hour}:${minute}`;
            } else {
              // 如果无法提取时间，使用分钟序号作为后备
              return `${item.minute}m`;
            }
          }
        } else {
          // 如果没有原始时间，使用分钟序号作为后备
          return `${item.minute}m`;
        }
      });
      
      const onlineUsersData = minuteStats.map(item => item.onlineUsers);
      const revenueData = minuteStats.map(item => item.revenue);
      const enterUsersData = minuteStats.map(item => item.enterUsers);
      const interactionUsersData = minuteStats.map(item => item.interactionUsers);

      // 根据选中的指标决定显示哪些数据系列
      const series = [];
      
      if (this.selectedMinuteMetric === 'onlineUsers' || this.selectedMinuteMetric === 'all') {
        series.push({
          name: this.$t('analyticsSingle.avgOnlineUsersPerMinute'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#10B981' // 使用primary颜色
          },
          itemStyle: {
            color: '#10B981',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(16, 185, 129, 0.4)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }
            ])
          },
          data: onlineUsersData
        });
      }
      
      if (this.selectedMinuteMetric === 'revenue' || this.selectedMinuteMetric === 'all') {
        series.push({
          name: this.$t('analyticsSingle.avgRevenuePerMinute'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#EC4899' // 粉色
          },
          itemStyle: {
            color: '#EC4899',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(236, 72, 153, 0.4)' },
              { offset: 1, color: 'rgba(236, 72, 153, 0.1)' }
            ])
          },
          data: revenueData,
          yAxisIndex: 1 // 使用第二个Y轴
        });
      }
      
      if (this.selectedMinuteMetric === 'enterUsers' || this.selectedMinuteMetric === 'all') {
        series.push({
          name: this.$t('analyticsSingle.avgEnterUsersPerMinute'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#3B82F6' // 蓝色
          },
          itemStyle: {
            color: '#3B82F6',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(59, 130, 246, 0.4)' },
              { offset: 1, color: 'rgba(59, 130, 246, 0.1)' }
            ])
          },
          data: enterUsersData
        });
      }
      
      if (this.selectedMinuteMetric === 'interactionUsers' || this.selectedMinuteMetric === 'all') {
        series.push({
          name: this.$t('analyticsSingle.avgInteractionUsersPerMinute'),
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 4,
          showSymbol: xAxisData.length <= 20,
          lineStyle: {
            width: 3,
            color: '#8B5CF6' // 紫色
          },
          itemStyle: {
            color: '#8B5CF6',
            borderColor: '#ffffff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(139, 92, 246, 0.4)' },
              { offset: 1, color: 'rgba(139, 92, 246, 0.1)' }
            ])
          },
          data: interactionUsersData
        });
      }

      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(42, 34, 57, 0.9)',
          borderColor: 'rgba(129, 140, 248, 0.3)',
          textStyle: {
            color: '#ffffff'
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: 'rgba(139, 92, 246, 0.5)'
            }
          },
          formatter: (params) => {
            let tooltip = `<div style="font-size: 12px; margin-bottom: 5px;">`;
            const index = params[0].dataIndex;
            
            // 显示原始时间，如果可用的话
            if (this.minuteStatsData && this.minuteStatsData.minuteStats && 
                index < this.minuteStatsData.minuteStats.length) {
              const minuteStat = this.minuteStatsData.minuteStats[index];
              if (minuteStat.originalMinute) {
                // 从"2026-01-18 18:29"格式中提取"18:29"，只显示小时和分钟
                const timeStr = minuteStat.originalMinute;
                const timeMatch = timeStr.match(/(\d{2}:\d{2})/);
                if (timeMatch) {
                  tooltip += `<strong>${timeMatch[0]}</strong></div>`;
                } else {
                  // 如果没有匹配到HH:MM格式，尝试其他格式
                  const altMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
                  if (altMatch) {
                    const hour = altMatch[1].padStart(2, '0');
                    const minute = altMatch[2];
                    tooltip += `<strong>${hour}:${minute}</strong></div>`;
                  } else {
                    tooltip += `<strong>${timeStr}</strong></div>`;
                  }
                }
              } else {
                tooltip += `<strong>${this.$t('analyticsSingle.minute', { index: index + 1 })}</strong></div>`;
              }
            } else {
              tooltip += `<strong>${this.$t('analyticsSingle.minute', { index: index + 1 })}</strong></div>`;
            }

            params.forEach(param => {
              const value = param.value || 0;
              const seriesName = param.seriesName;
              const color = param.color;

              tooltip += `
                <div style="display: flex; align-items: center; margin: 2px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 8px;"></span>
                  <span style="flex: 1;">${seriesName}:</span>
                  <span style="font-weight: bold; margin-left: 10px;">${this.formatNumber(value)}</span>
                </div>
              `;
            });

            return tooltip;
          }
        },
        legend: {
          data: series.map(s => s.name),
          textStyle: {
            color: '#9ca3af'
          },
          right: 10,
          top: 0,
          type: 'scroll' // 如果图例太多，可以滚动
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisData,
          axisLine: {
            lineStyle: {
              color: 'rgba(156, 163, 175, 0.3)'
            }
          },
          axisLabel: {
            color: '#9ca3af',
            fontSize: 11,
            // 控制X轴标签显示间隔，避免重叠 - 大幅减少标签密度
            interval: (index) => {
              const totalPoints = xAxisData.length;
              if (totalPoints <= 30) {
                return index % 10 === 0; // 每10个显示一个标签
              } else if (totalPoints <= 60) {
                return index % 15 === 0; // 每15个显示一个标签
              } else if (totalPoints <= 120) {
                return index % 25 === 0; // 每25个显示一个标签
              } else if (totalPoints <= 180) {
                return index % 40 === 0; // 每40个显示一个标签
              } else if (totalPoints <= 240) {
                return index % 60 === 0; // 每60个显示一个标签
              } else {
                return index % 80 === 0; // 每80个显示一个标签
              }
            },
            rotate: 45,
            overflow: 'break',
            margin: 15
          }
        },
        yAxis: [
          {
            type: 'value',
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#9ca3af',
              fontSize: 11,
              formatter: (value) => {
                if (value >= 1000) {
                  return (value / 1000).toFixed(0) + 'K';
                }
                return value;
              }
            },
            splitLine: {
              lineStyle: {
                color: 'rgba(156, 163, 175, 0.1)',
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            axisLine: {
              show: false
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: '#9ca3af',
              fontSize: 11,
              formatter: (value) => {
                return value;
              }
            },
            splitLine: {
              show: false
            },
            position: 'right'
          }
        ],
        series: series
      };
    },

    /**
     * 切换选中的分钟级指标
     */
    selectMinuteMetric(metric) {
      if (this.selectedMinuteMetric === metric) return;
      
      this.selectedMinuteMetric = metric;
      
      // 如果当前是分钟级视图，重新渲染图表
      if (this.performanceViewMode === 'avg-per-minute') {
        this.renderMinuteStatsChart();
      }
    },

    /**
     * 切换Gifter表格视图模式
     */
    switchGifterTableViewMode(mode) {
      if (this.gifterTableViewMode === mode) return;
      
      this.gifterTableViewMode = mode;
      
      // 根据选中的模式加载相应的数据
      if (mode === 'top-spenders' || mode === 'most-frequent') {
        // TOP SPENDERS和MOST FREQUENT使用相同的数据源，只是排序方式不同
        // 如果数据还没有加载，则加载数据
        if (!this.userParticipationRankingData && !this.userParticipationRankingLoading) {
          this.loadUserParticipationRanking();
        }
      } else if (mode === 'top-gifts') {
        this.loadGiftTypeRanking();
      }
    },

    /**
     * 加载礼物统计数据
     */
    async loadGiftStats() {
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载礼物统计数据：缺少sessionId或roomId');
        return;
      }

      this.giftStatsLoading = true;
      try {
        console.log('开始加载礼物统计数据，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId
        });

        // 调用liveDataService中的新方法
        const response = await liveDataService.getLiveSessionGiftStats(
          this.currentStreamerId,
          this.selectedSessionId
        );

        console.log('礼物统计数据响应:', response);

        if (response && response.success && response.data) {
          this.giftStatsData = response.data;
          console.log('礼物统计数据加载成功:', this.giftStatsData);
        } else {
          console.warn('礼物统计数据API返回数据格式不正确:', response);
          this.giftStatsData = null;
        }
      } catch (error) {
        console.error('加载礼物统计数据失败:', error);
        this.giftStatsData = null;
      } finally {
        this.giftStatsLoading = false;
      }
    },

    /**
     * 加载用户参与频次排行数据
     */
    async loadUserParticipationRanking() {
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载用户参与频次排行：缺少sessionId或roomId');
        return;
      }

      this.userParticipationRankingLoading = true;
      try {
        console.log('开始加载用户参与频次排行，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId
        });

        // 调用liveDataService中的新方法
        const response = await liveDataService.getUserParticipationRanking(
          this.currentStreamerId,
          this.selectedSessionId,
          20, // pageSize
          1   // pageIndex
        );

        console.log('用户参与频次排行响应:', response);

        if (response && response.success && response.data) {
          this.userParticipationRankingData = response.data;
          console.log('用户参与频次排行加载成功:', this.userParticipationRankingData);
        } else {
          console.warn('用户参与频次排行API返回数据格式不正确:', response);
          this.userParticipationRankingData = null;
        }
      } catch (error) {
        console.error('加载用户参与频次排行失败:', error);
        this.userParticipationRankingData = null;
      } finally {
        this.userParticipationRankingLoading = false;
      }
    },

    /**
     * 加载礼物类型排行数据
     */
    async loadGiftTypeRanking() {
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载礼物类型排行：缺少sessionId或roomId');
        return;
      }

      this.giftTypeRankingLoading = true;
      try {
        console.log('开始加载礼物类型排行，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId,
          pageSize: 20,
          pageIndex: 1
        });

        // 调用liveDataService中的新方法
        const response = await liveDataService.getGiftTypeRanking(
          this.currentStreamerId,
          this.selectedSessionId,
          20, // pageSize
          1   // pageIndex
        );

        console.log('礼物类型排行响应:', response);

        if (response && response.success && response.data) {
          // 转换API返回的数据格式以匹配模板期望的格式
          this.giftTypeRankingData = response.data.map((item, index) => ({
            giftTypeName: item.giftType || this.$t('analyticsSingle.unknown'),
            giftTypeId: index + 1, // 使用索引作为ID，因为API没有提供ID字段
            giftCount: item.count || 0,
            totalGiftValue: item.totalValue || 0,
            avgGiftValue: item.avgValue || 0,
            giftImgUrl: item.giftImgUrl || '', // 添加giftImgUrl字段
            // 保留原始数据字段，以防万一
            giftType: item.giftType,
            count: item.count,
            totalValue: item.totalValue,
            avgValue: item.avgValue
          }));
          console.log('礼物类型排行加载成功，转换后数据:', this.giftTypeRankingData);
        } else {
          console.warn('礼物类型排行API返回数据格式不正确:', response);
          this.giftTypeRankingData = null;
        }
      } catch (error) {
        console.error('加载礼物类型排行失败:', error);
        this.giftTypeRankingData = null;
      } finally {
        this.giftTypeRankingLoading = false;
      }
    },

    /**
     * 判断是否应该显示"Read more"按钮
     * 如果内容较短（少于150个字符或只有一行），就不显示
     */
    shouldShowReadMore() {
      const summary = this.getAnalysisSummary();
      // 如果内容为空或只有"数据正在整理中"这样的短提示，不显示Read more
      if (!summary || summary.length < 150) {
        return false;
      }
      
      // 检查换行符数量，如果有多行内容，可能需要展开
      const lineCount = (summary.match(/\n/g) || []).length + 1;
      return lineCount > 2 || summary.length > 300;
    },

    /**
     * 切换累计指标的选中状态
     */
    toggleCumulativeMetric(metric) {
      console.log('切换累计指标:', metric);
      
      const index = this.selectedCumulativeMetrics.indexOf(metric);
      if (index === -1) {
        // 如果指标不在选中列表中，添加它
        this.selectedCumulativeMetrics.push(metric);
      } else {
        // 如果指标已经在选中列表中，移除它
        this.selectedCumulativeMetrics.splice(index, 1);
      }
      
      console.log('更新后的选中指标:', this.selectedCumulativeMetrics);
      
      // 重新渲染图表
      if (this.performanceViewMode === 'cumulative' && this.totalStatsData) {
        this.renderTotalStatsChart();
      }
    },

    /**
     * 根据排序方式获取用户参与数据
     * @param {string} sortBy - 排序方式：'totalActions'、'giftCount' 或 'totalGiftValue'
     * @returns {Array} 排序后的用户参与数据
     */
    getSortedUserParticipationData(sortBy) {
      if (!this.userParticipationRankingData || !Array.isArray(this.userParticipationRankingData)) {
        return [];
      }

      // 创建数据副本以避免修改原始数据
      const dataCopy = [...this.userParticipationRankingData];

      if (sortBy === 'totalActions') {
        // 按总参与次数排序（参与次数 + 消息次数 + 礼物次数）
        return dataCopy.sort((a, b) => {
          const totalA = (a.participationCount || 0) + (a.messageCount || 0) + (a.giftCount || 0);
          const totalB = (b.participationCount || 0) + (b.messageCount || 0) + (b.giftCount || 0);
          return totalB - totalA; // 降序排列
        });
      } else if (sortBy === 'giftCount') {
        // 按礼物数量排序
        return dataCopy.sort((a, b) => {
          const giftCountA = a.giftCount || 0;
          const giftCountB = b.giftCount || 0;
          return giftCountB - giftCountA; // 降序排列
        });
      } else if (sortBy === 'totalGiftValue') {
        // 按总礼物价值排序
        return dataCopy.sort((a, b) => {
          const giftValueA = a.totalGiftValue || 0;
          const giftValueB = b.totalGiftValue || 0;
          return giftValueB - giftValueA; // 降序排列
        });
      }

      // 默认返回原始数据
      return dataCopy;
    },

    /**
     * 加载直播流程详情（transcription summary）
     */
    async loadTranscriptionSummary() {
      if (!this.selectedSessionId || !this.currentStreamerId) {
        console.warn('无法加载直播流程详情：缺少sessionId或roomId');
        return;
      }

      this.transcriptionSummaryLoading = true;
      try {
        console.log('开始加载直播流程详情，参数:', {
          liveRoomId: this.currentStreamerId,
          sessionId: this.selectedSessionId
        });

        // 调用liveDataService中的新方法
        const response = await liveDataService.getLiveSessionTranscriptionSummary(
          this.currentStreamerId,
          this.selectedSessionId
        );

        console.log('直播流程详情响应:', response);

        if (response && response.success && response.data) {
          this.transcriptionSummaryData = response.data;
          console.log('直播流程详情加载成功:', this.transcriptionSummaryData);
        } else {
          console.warn('直播流程详情API返回数据格式不正确:', response);
          this.transcriptionSummaryData = null;
        }
      } catch (error) {
        console.error('加载直播流程详情失败:', error);
        this.transcriptionSummaryData = null;
      } finally {
        this.transcriptionSummaryLoading = false;
      }
    },

    /**
     * 获取直播流程详情段落数据
     * @returns {Array} 解析后的段落数据
     */
    getTranscriptionParagraphs() {
      if (!this.transcriptionSummaryData || !this.transcriptionSummaryData.summaryAttributes) {
        return [];
      }

      try {
        // 解析summaryAttributes JSON字符串，使用更健壮的解析方法
        const summaryAttributes = this.parseSummaryAttributes(this.transcriptionSummaryData.summaryAttributes);
        return summaryAttributes.paragraphs || [];
      } catch (error) {
        console.error('解析summaryAttributes失败:', error);
        return [];
      }
    },

    /**
     * 获取直播主题
     * @returns {string} 直播主题
     */
    getTranscriptionTheme() {
      if (!this.transcriptionSummaryData || !this.transcriptionSummaryData.summaryAttributes) {
        return '';
      }

      try {
        const summaryAttributes = this.parseSummaryAttributes(this.transcriptionSummaryData.summaryAttributes);
        return summaryAttributes.liveTheme || '';
      } catch (error) {
        console.error('解析直播主题失败:', error);
        return '';
      }
    },

    /**
     * 获取整体总结
     * @returns {string} 整体总结
     */
    getTranscriptionOverallSummary() {
      if (!this.transcriptionSummaryData || !this.transcriptionSummaryData.summaryAttributes) {
        return '';
      }

      try {
        const summaryAttributes = this.parseSummaryAttributes(this.transcriptionSummaryData.summaryAttributes);
        return summaryAttributes.overallSummary || '';
      } catch (error) {
        console.error('解析整体总结失败:', error);
        return '';
      }
    },

    /**
     * 解析summaryAttributes JSON字符串，处理可能的格式问题
     * @param {string} jsonString - 要解析的JSON字符串
     * @returns {Object} 解析后的对象
     */
    parseSummaryAttributes(jsonString) {
      if (!jsonString || typeof jsonString !== 'string') {
        return {};
      }

      try {
        // 首先尝试直接解析
        return JSON.parse(jsonString);
      } catch (error) {
        console.warn('直接解析JSON失败，尝试修复格式:', error.message);
        console.warn('原始JSON字符串:', jsonString);
        
        // 尝试修复常见的JSON格式问题
        let fixedJson = jsonString;
        
        // 1. 移除BOM字符（如果存在）
        if (fixedJson.charCodeAt(0) === 0xFEFF) {
          fixedJson = fixedJson.slice(1);
        }
        
        // 2. 如果字符串以单引号开头和结尾，替换为双引号
        if (fixedJson.startsWith("'") && fixedJson.endsWith("'")) {
          fixedJson = fixedJson.substring(1, fixedJson.length - 1);
          // 转义内部的双引号
          fixedJson = fixedJson.replace(/"/g, '\\"');
          // 用双引号重新包装
          fixedJson = `"${fixedJson}"`;
        }
        
        // 3. 修复未转义的单引号（在双引号字符串内部）
        // 匹配双引号内的内容，将未转义的单引号转义
        fixedJson = fixedJson.replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, (match, content) => {
          // 转义内容中的单引号
          const escapedContent = content.replace(/'/g, "\\'");
          return `"${escapedContent}"`;
        });
        
        // 4. 修复未转义的反斜杠
        fixedJson = fixedJson.replace(/\\(?!["\\/bfnrtu])/g, '\\\\');
        
        // 5. 修复控制字符（保留换行符和制表符）
        fixedJson = fixedJson.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '');
        
        // 6. 修复多余的逗号（数组或对象最后一个元素后的逗号）
        fixedJson = fixedJson
          .replace(/,\s*]/g, ']')
          .replace(/,\s*}/g, '}');
        
        // 7. 修复未闭合的字符串
        // 统计双引号数量，如果是奇数，添加一个闭合引号
        const quoteCount = (fixedJson.match(/"/g) || []).length;
        if (quoteCount % 2 !== 0) {
          fixedJson += '"';
        }
        
        // 8. 尝试解析修复后的JSON
        try {
          return JSON.parse(fixedJson);
        } catch (secondError) {
          console.error('修复后解析JSON仍然失败:', secondError.message);
          console.error('修复后的JSON字符串:', fixedJson);
          
          // 作为最后的手段，尝试多种方式提取overallSummary字段
          let overallSummary = '';
          
          // 方式1：正则匹配双引号包裹的overallSummary
          const overallSummaryMatch1 = jsonString.match(/"overallSummary"\s*:\s*"([^"]*)"/);
          if (overallSummaryMatch1 && overallSummaryMatch1[1]) {
            overallSummary = overallSummaryMatch1[1];
          } else {
            // 方式2：正则匹配单引号包裹的overallSummary
            const overallSummaryMatch2 = jsonString.match(/'overallSummary'\s*:\s*'([^']*)'/);
            if (overallSummaryMatch2 && overallSummaryMatch2[1]) {
              overallSummary = overallSummaryMatch2[1];
            } else {
              // 方式3：尝试查找overallSummary字段后的内容
              const overallSummaryIndex = jsonString.indexOf('"overallSummary"');
              if (overallSummaryIndex !== -1) {
                const afterOverallSummary = jsonString.substring(overallSummaryIndex + 16);
                const colonIndex = afterOverallSummary.indexOf(':');
                if (colonIndex !== -1) {
                  const valueStart = afterOverallSummary.substring(colonIndex + 1).trim();
                  // 尝试提取字符串值
                  if (valueStart.startsWith('"')) {
                    const endQuoteIndex = valueStart.indexOf('"', 1);
                    if (endQuoteIndex !== -1) {
                      overallSummary = valueStart.substring(1, endQuoteIndex);
                    }
                  } else if (valueStart.startsWith("'")) {
                    const endQuoteIndex = valueStart.indexOf("'", 1);
                    if (endQuoteIndex !== -1) {
                      overallSummary = valueStart.substring(1, endQuoteIndex);
                    }
                  }
                }
              }
            }
          }
          
          // 如果成功提取到overallSummary，返回包含该字段的对象
          if (overallSummary) {
            console.log('成功提取overallSummary:', overallSummary);
            return {
              overallSummary: overallSummary,
              paragraphs: [],
              liveTheme: ''
            };
          }
          
          // 如果连提取都失败，返回空对象
          console.warn('无法提取任何字段，返回空对象');
          return {};
        }
      }
    }
  },
  mounted() {
    console.log('Analytics组件已挂载');
    this.isComponentMounted = true;

    // 直接加载分析数据，页面框架立即显示
    this.loadAnalyticsData();

    // 监听主播切换事件
    this.$root.$on('streamer-changed', this.handleStreamerChanged);

    // 添加滚动事件监听（金主列表无限加载）
    window.addEventListener('scroll', this.handleWindowScroll);

    // 添加窗口大小变化监听，用于响应式布局
    window.addEventListener('resize', this.handleWindowResize);

    // 初始化头像懒加载观察器
    this.initAvatarObserver();

    // 图表现在会在数据加载完成后自动初始化，不需要提前初始化
  },
  beforeDestroy() {
    console.log('Analytics组件即将销毁');
    this.isComponentMounted = false;

    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    // 移除事件监听
    this.$root.$off('streamer-changed', this.handleStreamerChanged);

    // 移除滚动事件监听
    window.removeEventListener('scroll', this.handleWindowScroll);

    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.handleWindowResize);
  },
}
</script>

<style scoped>
.glass-panel {
  background: rgba(241, 245, 249, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 1rem;
}

.dark .glass-panel {
  background: rgba(42, 34, 57, 0.5);
  border: 1px solid rgba(129, 140, 248, 0.2);
}

/* 自定义滚动条样式 */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 4px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
  background-clip: content-box;
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out;
}

/* 渐变文字效果 */
.gradient-text {
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 卡片悬停效果 */
.card-hover {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Sentiment Reactions 卡片样式 */
.sentiment-card {
  min-height: 60px;
  transition: all 0.3s ease;
}

.sentiment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px -8px rgba(139, 92, 246, 0.3);
}

/* 数字徽章样式优化 */
.sentiment-badge {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* 确保容器不会产生横向滚动 */
main {
  overflow-x: hidden;
  max-width: 100%;
  width: 100%;
}

/* 菊花loading图标样式 */
.loading-spinner {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  position: relative;
  display: inline-block;
}

.loading-spinner::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(
    transparent 0deg,
    transparent 270deg,
    #8b5cf6 360deg
  );
  animation: loading-spin 1s linear infinite;
}

.loading-spinner::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  border-radius: 50%;
  background: rgba(42, 34, 57, 0.9);
}

@keyframes loading-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Session Nodes 收起/展开样式 */
.session-sidebar {
  transition: width 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}

.session-sidebar-collapsed {
  width: 72px !important;
}

/* 展开/收起功能样式 */
.expand-toggle {
  display: none;
}

.expand-content {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expand-toggle:checked + .expand-content {
  -webkit-line-clamp: unset;
  display: block;
}

.expand-btn .more { display: inline; }
.expand-btn .less { display: none; }
.expand-toggle:checked ~ .expand-btn .more { display: none; }
.expand-toggle:checked ~ .expand-btn .less { display: inline; }

/* 响应式文本大小调整 */
@media (max-width: 768px) {
  .sentiment-card span {
    font-size: 0.75rem;
  }
}
</style>
